// The Maya site workbench, ported verbatim from the pre-Vue www/assets/js/app.js (wbSiteStudio,
// pwOpen, wsRedesign, mdLite). It is imperative DOM on purpose: the drawer's behaviour — toolbar
// sentences to Maya, Pages|Design sidebar, live skin/colour swap, Text-mode hover chrome, block
// add/move/delete, pending-edit painting, blog manager, 3-step publish wizard — was tuned as raw
// DOM code against the existing .ws-*/.pw-* CSS; rewriting it declaratively is how the effect
// drifted in the first place. Vue only owns the drawer shell (MayaView) and calls mount/destroy.

function esc(v) {
  return String(v == null ? "" : v).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

const SCAN_POLL_MS = 10000;
const SCAN_POLL_MAX = 180;

// Preview errors are rendered inside the iframe as JSON, so the parent page
// cannot inspect the HTTP status directly. Keep this check narrow: other
// preview errors should remain visible to the normal error handling path.
export function isPendingPreviewUnavailableDocument(doc) {
  const text = String(doc?.body?.textContent || doc?.body?.innerText || "").toLowerCase();
  return text.includes("pending preview unavailable") && (text.includes('"gone"') || text.includes("gone"));
}

// ── mdLite: the same tiny renderer the old chat/drawer used (bold, links, tables, lists, headings).
// Article bodies and usage notes are rendered with it inside the workbench only.
export function mdLite(s, opts, hooks) {
  opts = opts || {};
  hooks = hooks || {};
  s = esc(s);
  function inl(x) {
    x = x.replace(/\*\*([\s\S]+?)\*\*/g, "<b>$1</b>");
    x = x.replace(/(https?:\/\/[^\s<)。，；]+)/g, (u) => {
      const ds = hooks.delivSlug ? hooks.delivSlug(u) : "";
      if (ds) return '<button type="button" class="mlink mlink-wb" data-mdeliv="' + esc(ds) + '">' + esc(hooks.t ? hooks.t("wb_open_here") : "Open here") + "</button>";
      let sh = u.replace(/^https?:\/\//, "");
      if (sh.length > 46) sh = sh.slice(0, 44) + "…";
      return '<a href="' + u + '" target="_blank" rel="noopener" class="mlink">' + sh + "</a>";
    });
    return x;
  }
  const isPipe = (l) => /^\s*\|/.test(l);
  const isSep = (l) => /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(l) && /-/.test(l) && /\|/.test(l);
  const isH = (l) => /^\s*#{1,6}\s+/.test(l);
  const isUl = (l) => /^\s*[-*]\s+/.test(l);
  const isOl = (l) => /^\s*\d+\.\s+/.test(l);
  const cells = (r) => r.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
  const lines = s.split("\n");
  const out = [];
  let i = 0;
  const n = lines.length;
  while (i < n) {
    const ln = lines[i];
    if (isPipe(ln)) {
      let j = i;
      const blk = [];
      while (j < n && isPipe(lines[j])) { blk.push(lines[j]); j++; }
      const runsToEnd = j >= n;
      const hasTable = blk.length >= 2 && isSep(blk[1]);
      if ((opts.streaming && runsToEnd) || !hasTable) { out.push('<pre class="mdraw">' + blk.join("\n") + "</pre>"); i = j; continue; }
      const head = cells(blk[0]);
      let h = '<div class="mdtblwrap"><table class="mdtbl"><thead><tr>';
      head.forEach((c) => { h += "<th>" + inl(c) + "</th>"; });
      h += "</tr></thead><tbody>";
      for (let k = 2; k < blk.length; k++) {
        if (blk[k].trim() === "") continue;
        const rc = cells(blk[k]);
        h += "<tr>";
        for (let ci = 0; ci < head.length; ci++) h += "<td>" + inl(rc[ci] != null ? rc[ci] : "") + "</td>";
        h += "</tr>";
      }
      h += "</tbody></table></div>";
      out.push(h);
      i = j;
      continue;
    }
    if (isH(ln)) { out.push('<b class="mdh">' + inl(ln.replace(/^\s*#{1,6}\s+/, "")) + "</b>"); i++; continue; }
    if (isUl(ln)) { const ui = []; while (i < n && isUl(lines[i])) { ui.push("<li>" + inl(lines[i].replace(/^\s*[-*]\s+/, "")) + "</li>"); i++; } out.push("<ul>" + ui.join("") + "</ul>"); continue; }
    if (isOl(ln)) {
      const oli = [];
      const st0 = parseInt((/^\s*(\d+)\./.exec(ln) || [0, "1"])[1], 10) || 1;
      let j2 = i;
      while (j2 < n) {
        if (isOl(lines[j2])) { oli.push("<li>" + inl(lines[j2].replace(/^\s*\d+\.\s+/, "")) + "</li>"); i = j2 + 1; j2 = i; continue; }
        if (lines[j2].trim() === "") { j2++; continue; }
        break;
      }
      out.push("<ol" + (st0 !== 1 ? ' start="' + st0 + '"' : "") + ">" + oli.join("") + "</ol>");
      continue;
    }
    if (ln.trim() === "") { i++; continue; }
    const para = [ln];
    i++;
    while (i < n && lines[i].trim() !== "" && !isPipe(lines[i]) && !isH(lines[i]) && !isUl(lines[i]) && !isOl(lines[i])) { para.push(lines[i]); i++; }
    out.push("<p>" + inl(para.join("<br>")) + "</p>");
  }
  return out.join("");
}

// The 3-step publish wizard (IMP-G): address → site info + live SERP preview → review + publish.
// Ported from pwOpen(); the overlay is appended to document.body, exactly like before.
function pwOpen(brandId, onPub, ctx) {
  const { api, t, toast, upgrade, copy } = ctx;
  const st = { step: 1, slug: "", title: "", desc: "", tdef: "", ddef: "", surface: { dev: false, apex: "", path_url: "", suffix: "soarank.app" }, published: false, unc: 0, uncList: [], editingSlug: false, slugErr: "", slugWarn: "", live: "" };
  const ov = document.createElement("div");
  ov.className = "pw-ov";
  ov.setAttribute("role", "dialog");
  ov.setAttribute("aria-modal", "true");
  ov.innerHTML = '<div class="pw-box"><div class="pw-head"><h3>' + esc(t("pw_title")) + '</h3><div class="pw-dots" id="pwDots"></div><button type="button" class="pw-close" aria-label="' + esc(t("pw_close")) + '">&times;</button></div><div class="pw-body" id="pwBody"></div><div class="pw-foot" id="pwFoot"></div></div>';
  document.body.appendChild(ov);
  function close() { ov.remove(); document.removeEventListener("keydown", onKey); }
  function onKey(ev) { if (ev.key === "Escape") { ev.stopPropagation(); close(); } }
  document.addEventListener("keydown", onKey);
  ov.addEventListener("mousedown", (ev) => { if (ev.target === ov) close(); });
  ov.querySelector(".pw-close").addEventListener("click", close);
  function pwStripProto(u) { u = u || ""; const i = u.indexOf("://"); if (i >= 0) u = u.slice(i + 3); if (u.charAt(u.length - 1) === "/") u = u.slice(0, -1); return u; }
  function serpUrl() { const s = st.surface || {}; return pwStripProto(s.dev ? (s.path_url || "") : (s.apex || "")); }
  function dotsHtml() { let h = ""; for (let i = 1; i <= 3; i++) h += '<span class="pw-dot' + (i === st.step ? " on" : "") + '"></span>'; return h; }
  function renderStep1() {
    const b = document.getElementById("pwBody");
    const s = st.surface || {};
    let big;
    if (s.dev) {
      big = '<div class="pw-urlbig">' + esc(pwStripProto(s.path_url)) + "</div>" +
        '<div class="pw-note">' + esc(t("pw_dev_note").split("{b}").join(st.slug + "." + (s.suffix || "soarank.app"))) + "</div>";
    } else {
      big = '<div class="pw-urlbig">https://<b>' + esc(st.slug) + "</b>." + esc(s.suffix || "soarank.app") + "</div>";
    }
    let slugBlock;
    if (st.editingSlug) {
      slugBlock = '<div class="pw-slugrow"><input id="pwSlug" value="' + esc(st.slug) + '" maxlength="40" autocomplete="off" spellcheck="false"><button type="button" class="pw-pencil" id="pwSlugSave">' + esc(t("pw_save")) + '</button><button type="button" class="pw-pencil" id="pwSlugCancel">' + esc(t("pw_cancel")) + '</button></div><div class="pw-note">' + esc(t("pw_slug_rule")) + "</div>";
    } else {
      slugBlock = '<div class="pw-slugrow"><button type="button" class="pw-pencil" id="pwSlugEdit">&#9998; ' + esc(t("pw_edit_addr")) + "</button></div>";
    }
    const err = st.slugErr ? '<div class="pw-err">' + esc(st.slugErr) + "</div>" : "";
    const warn = st.slugWarn ? '<div class="pw-warn">' + esc(st.slugWarn) + "</div>" : "";
    b.innerHTML = '<div class="pw-step-l">' + esc(t("pw_s1_l")) + '</div><div class="pw-note" style="margin-top:0;margin-bottom:10px">' + esc(t("pw_live_at")) + "</div>" + big + err + warn + slugBlock +
      '<button type="button" class="pw-lock" id="pwCustom"><span class="pw-lockt">&#128274; ' + esc(t("pw_customdom")) + '</span><span class="pw-tag">' + esc(t("pw_managed")) + "</span></button>";
    const e1 = document.getElementById("pwSlugEdit");
    if (e1) e1.addEventListener("click", () => { st.editingSlug = true; st.slugErr = ""; st.slugWarn = ""; render(); });
    const e2 = document.getElementById("pwSlugCancel");
    if (e2) e2.addEventListener("click", () => { st.editingSlug = false; st.slugErr = ""; render(); });
    const e3 = document.getElementById("pwSlugSave");
    if (e3) e3.addEventListener("click", saveSlug);
    const cd = document.getElementById("pwCustom");
    if (cd) cd.addEventListener("click", () => { window.open(ctx.prefix() + "/pricing.html", "_blank", "noopener"); });
  }
  function saveSlug() {
    const inp = document.getElementById("pwSlug");
    if (!inp) return;
    const v = (inp.value || "").trim().toLowerCase();
    if (!/^[a-z0-9]([a-z0-9-]{1,38}[a-z0-9])$/.test(v)) { st.slugErr = t("pw_slug_rule"); render(); return; }
    const save = document.getElementById("pwSlugSave");
    if (save) save.disabled = true;
    st.slugErr = "";
    api("/api/site/slug", { method: "POST", json: { brand_id: brandId, slug: v } }).then((j) => {
      if (j && j.ok) {
        st.slug = j.slug;
        if (j.surface) st.surface = j.surface;
        st.editingSlug = false;
        st.slugWarn = j.was_published && !j.unchanged ? t("pw_slug_warn_pub") + " " + t("pw_slug_migrating") : "";
        render();
        return;
      }
      if (j && j.error === "taken") st.slugErr = t("pw_slug_taken");
      else if (j && (j.upgrade || j.error === "upgrade")) { close(); upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      else st.slugErr = t("pw_slug_rule");
      if (save) save.disabled = false;
      render();
    }).catch(() => { if (save) save.disabled = false; st.slugErr = t("err_net"); render(); });
  }
  function renderStep2() {
    const b = document.getElementById("pwBody");
    const tv = st.title || st.tdef || "";
    const dv = st.desc || st.ddef || "";
    b.innerHTML = '<div class="pw-step-l">' + esc(t("pw_s2_l")) + "</div>" +
      '<div class="pw-fld"><label><span>' + esc(t("pw_title_f")) + '</span><span class="pw-ct" id="pwTc"></span></label><input id="pwTitle" maxlength="90" value="' + esc(tv) + '"></div>' +
      '<div class="pw-fld"><label><span>' + esc(t("pw_desc_f")) + '</span><span class="pw-ct" id="pwDc"></span></label><textarea id="pwDesc" maxlength="240">' + esc(dv) + "</textarea></div>" +
      '<div class="pw-serp"><div class="pw-serp-l">' + esc(t("pw_serp_l")) + '</div><div class="pw-serp-t" id="pwSerpT"></div><div class="pw-serp-u" id="pwSerpU"></div><div class="pw-serp-d" id="pwSerpD"></div></div>' +
      '<div class="pw-err" id="pwMetaErr" style="display:none"></div>';
    const ti = document.getElementById("pwTitle");
    const de = document.getElementById("pwDesc");
    function sync() { st.title = ti.value || ""; st.desc = de.value || ""; paintSerp(); paintCounts(); }
    function paintCounts() {
      const tc = document.getElementById("pwTc");
      const dc = document.getElementById("pwDc");
      const tl = (ti.value || "").length;
      const dl = (de.value || "").length;
      tc.textContent = tl + " / 60";
      dc.textContent = dl + " / 160";
      tc.className = "pw-ct" + (tl > 60 ? " over" : "");
      dc.className = "pw-ct" + (dl > 160 ? " over" : "");
    }
    function paintSerp() {
      document.getElementById("pwSerpT").textContent = ti.value || st.tdef || st.slug;
      document.getElementById("pwSerpU").textContent = serpUrl();
      document.getElementById("pwSerpD").textContent = de.value || st.ddef || "";
    }
    ti.addEventListener("input", sync);
    de.addEventListener("input", sync);
    paintSerp();
    paintCounts();
  }
  function renderStep3() {
    const b = document.getElementById("pwBody");
    const tv = st.title || st.tdef || "";
    const dv = st.desc || st.ddef || "";
    let red = "";
    if (st.unc > 0) {
      const items = (st.uncList || []).map((u) => "<li>" + esc((u.page || "") + " · " + (u.label || u.field || "")) + "</li>").join("");
      red = '<div class="pw-redbar"><div class="pw-rb-h" id="pwRbH">&#9888; ' + esc(t("pw_unconf_h").split("{n}").join(st.unc)) + ' <span style="font-weight:400;text-decoration:underline">' + esc(t("pw_unconf_show")) + '</span></span></div><div class="pw-note" style="margin-top:6px">' + esc(t("pw_unconf_sub")) + '</div><ul id="pwRbL" style="display:none">' + items + "</ul></div>";
    }
    b.innerHTML = '<div class="pw-step-l">' + esc(t("pw_s3_l")) + '</div><ul class="pw-sum">' +
      '<li><span class="pw-sk">' + esc(t("pw_review_url")) + '</span><span class="pw-sv">' + esc(serpUrl()) + "</span></li>" +
      '<li><span class="pw-sk">' + esc(t("pw_review_title")) + '</span><span class="pw-sv">' + esc(tv) + "</span></li>" +
      '<li><span class="pw-sk">' + esc(t("pw_review_desc")) + '</span><span class="pw-sv">' + esc(dv) + "</span></li></ul>" + red;
    const rh = document.getElementById("pwRbH");
    if (rh) rh.addEventListener("click", () => { const l = document.getElementById("pwRbL"); if (l) l.style.display = l.style.display === "none" ? "block" : "none"; });
  }
  function renderDone() {
    const b = document.getElementById("pwBody");
    b.innerHTML = '<div class="pw-done"><div class="pw-check">&#10003;</div><div class="pw-step-l" style="text-align:center">' + esc(t("pw_done_h")) + '</div><div class="pw-durl">' + esc(pwStripProto(st.live || serpUrl())) + '</div><div class="pw-note pw-donenote">' + esc(t("pw_done_note")) + "</div></div>";
    const f = document.getElementById("pwFoot");
    f.innerHTML = '<div class="pw-sp"></div><button type="button" class="pw-btn" id="pwCopy">' + esc(t("pw_copy")) + '</button><a class="pw-btn pw-primary" id="pwView" href="' + esc(st.live || "#") + '" target="_blank" rel="noopener">' + esc(t("pw_view")) + "</a>";
    document.getElementById("pwCopy").addEventListener("click", () => {
      const c = document.getElementById("pwCopy");
      copy(st.live || serpUrl());
      c.textContent = t("pw_copied");
      setTimeout(() => { c.textContent = t("pw_copy"); }, 1400);
    });
  }
  function metaErr(msg) {
    const nx = document.getElementById("pwNext");
    if (nx) nx.disabled = false;
    const e = document.getElementById("pwMetaErr");
    if (e) { e.textContent = msg; e.style.display = ""; }
  }
  function saveMeta(next) {
    const e = document.getElementById("pwMetaErr");
    if (e) e.style.display = "none";
    api("/api/site/meta", { method: "POST", json: { brand_id: brandId, title: st.title || st.tdef || "", desc: st.desc || st.ddef || "" } }).then((j) => {
      if (j && (j.upgrade || j.error === "upgrade")) { close(); upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (j && j.ok) { if (next) next(); return; }
      metaErr(t("pw_meta_fail"));
    }).catch(() => metaErr(t("err_net")));
  }
  function doPublish() {
    const pb = document.getElementById("pwPub");
    if (pb) { pb.disabled = true; pb.textContent = t("pw_publishing"); }
    const armed = !!st._pubArmed;
    function finishPublish(j) {
      if (j && j.ok) { st.live = j.live_url || ""; st.published = true; st.step = 4; render(); if (onPub) { try { onPub(st.live); } catch (_) { /* host callback */ } } return; }
      if (j && (j.upgrade || j.error === "upgrade")) { close(); upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (j && j.needs_confirm && !armed) {
        st.unc = j.needs_confirm;
        st.uncList = j.unconfirmed_list || [];
        st._pubArmed = true;
        render();
        const np = document.getElementById("pwPub");
        if (np) { np.disabled = false; np.textContent = t("pw_publish"); }
        toast(t("pw_unconf_h").split("{n}").join(String(j.needs_confirm)), t("pw_unconf_sub"), "warn");
        return;
      }
      if (pb) { pb.disabled = false; pb.textContent = t("pw_publish"); }
      toast(t("pw_pub_fail"), (j && j.error) || "", "warn");
    }
    function pollPublish(job, tries) {
      api("/api/jobs/" + encodeURIComponent(job)).then((j) => {
        if (j && j.state === "done") { finishPublish(j); return; }
        if (!j || j.state === "failed" || tries < 1) { finishPublish({ error: (j && j.reason) || "publish failed" }); return; }
        setTimeout(() => pollPublish(job, tries - 1), 2000);
      }).catch(() => finishPublish({ error: "publish failed" }));
    }
    api("/api/site/publish", { method: "POST", json: { brand_id: brandId, force: armed ? 1 : 0 } }).then((j) => {
      if (j && j.job) { pollPublish(j.job, 180); return; }
      finishPublish(j);
    }).catch(() => { if (pb) { pb.disabled = false; pb.textContent = t("pw_publish"); } toast(t("pw_pub_fail"), "", "warn"); });
  }
  function renderFoot() {
    if (st.step === 4) return;
    const f = document.getElementById("pwFoot");
    let left = "";
    if (st.step > 1) left = '<button type="button" class="pw-btn" id="pwBack">' + esc(t("pw_back")) + "</button>";
    let right;
    if (st.step < 3) right = '<button type="button" class="pw-btn pw-primary" id="pwNext">' + esc(t("pw_next")) + "</button>";
    else right = '<button type="button" class="pw-btn pw-primary" id="pwPub">' + esc(t("pw_publish")) + "</button>";
    f.innerHTML = left + '<div class="pw-sp"></div>' + right;
    const bk = document.getElementById("pwBack");
    if (bk) bk.addEventListener("click", () => { st._pubArmed = false; st.step--; render(); });
    const nx = document.getElementById("pwNext");
    if (nx) nx.addEventListener("click", () => {
      if (st.step === 1) { st.step = 2; render(); } else if (st.step === 2) { nx.disabled = true; saveMeta(() => { st._pubArmed = false; st.step = 3; render(); }); }
    });
    const pu = document.getElementById("pwPub");
    if (pu) pu.addEventListener("click", doPublish);
  }
  function render() {
    const dots = document.getElementById("pwDots");
    if (dots) dots.innerHTML = st.step <= 3 ? dotsHtml() : "";
    if (st.step === 1) renderStep1();
    else if (st.step === 2) renderStep2();
    else if (st.step === 3) renderStep3();
    else { renderDone(); return; }
    renderFoot();
  }
  document.getElementById("pwBody").innerHTML = '<div class="pw-note">' + esc(t("loading") || "Loading…") + "</div>";
  api("/api/site/meta?brand_id=" + encodeURIComponent(brandId)).then((j) => {
    if (!j || !j.ok || !j.has_draft) { close(); toast(t("pw_nodraft"), "", "warn"); return; }
    st.slug = j.slug || "";
    st.title = j.title || "";
    st.desc = j.desc || "";
    st.tdef = j.title_default || "";
    st.ddef = j.desc_default || "";
    st.surface = j.surface || st.surface;
    st.published = !!j.published;
    st.unc = j.unconfirmed || 0;
    st.uncList = j.unconfirmed_list || [];
    render();
  }).catch(() => { close(); toast(t("pw_nodraft"), "", "warn"); });
  return { close };
}

// One-click publish of a deliverable article (blog). Ported from articlePublish(): label capture,
// unconfirmed gate with inline note, upgrade bubble, "Blog is live" card into the conversation.
function articlePublish(d, card, onDone, ctx) {
  const { api, t, toast, upgrade } = ctx;
  const pb = card.querySelector(".art-publish");
  if (!pb) return;
  const lbl = pb.textContent;
  const wasPub = !!d.published;
  pb.disabled = true;
  pb.textContent = t("art_pubing") || "Publishing…";
  api("/api/deliverables/" + encodeURIComponent(d.slug) + "/publish", { method: "POST", json: { force: 0 } }).then((r) => {
    if (r && r.ok) {
      d.published = true;
      d.published_url = r.url;
      const pl = card.querySelector(".art-publine");
      if (pl) {
        pl.style.display = "";
        pl.innerHTML = '<span class="art-pubbadge">✓ ' + (wasPub ? esc(t("art_republished")) : esc(t("art_published"))) + "</span>" + (r.url ? ' <a href="' + esc(r.url) + '" target="_blank" rel="noopener">' + esc(r.url) + "</a>" : "");
      }
      if (wasPub) { pb.disabled = false; pb.textContent = lbl; }
      else {
        const live = document.createElement("a");
        live.className = "mc-cta";
        live.href = r.url;
        live.target = "_blank";
        live.rel = "noopener";
        live.textContent = t("art_view_live");
        pb.parentNode.replaceChild(live, pb);
      }
      if (r.card && ctx.pushCard) { try { ctx.pushCard(r.card); } catch (_) { /* host render */ } }
      toast(wasPub ? t("art_republished") : t("art_published_ok") || "Blog is live", r.url || "", "ok");
      if (onDone) { try { onDone(r); } catch (_) { /* host callback */ } }
    } else if (r && r.upgrade) {
      pb.disabled = false;
      pb.textContent = lbl;
      upgrade(r.msg, r.url);
    } else if (r && r.error === "unconfirmed") {
      pb.disabled = false;
      pb.textContent = lbl;
      const pu = card.querySelector(".art-publine");
      if (pu) {
        pu.style.display = "";
        pu.innerHTML = '<div class="pw-err">' + esc(r.msg || "") + "</div>";
      } else toast(r.msg || "", "", "warn");
    } else {
      pb.disabled = false;
      pb.textContent = lbl;
      toast((r && (r.msg || r.error)) || "failed", "", "warn");
    }
  }).catch(() => { pb.disabled = false; pb.textContent = lbl; toast("failed", "", "warn"); });
}

// ── the site workbench itself ─────────────────────────────────────────────────────────────────
export function mountSiteWorkbench(root, brandId, opts) {
  const ctx = {
    api: opts.api,
    t: opts.t,
    toast: opts.toast,
    upgrade: opts.upgrade,
    copy: opts.copy || ((txt) => { try { navigator.clipboard?.writeText(txt); } catch (_) { /* optional */ } }),
    prefix: opts.prefix || (() => ""),
    pushCard: opts.pushCard,
  };
  const t = ctx.t;
  function lang() { return opts.lang ? opts.lang() : "en"; }
  root.classList.add("ws-root");
  root.style.cssText = "flex:1 1 auto;min-height:0;display:flex;flex-direction:column";
  let alive = true;

  let page = "home";
  let mode = "view";
  let tab = "design";

  root.innerHTML =
    '<div class="ws-bar">'
    + '<label class="ws-pl">' + esc(t("studio_page")) + ' <select class="ws-page"></select></label>'
    + '<button type="button" class="ws-btn ws-addpage">＋ ' + esc(t("ws_pg_new")) + "</button>"
    + '<button type="button" class="ws-btn ws-addblog">＋ ' + esc(t("ws_add_blog")) + "</button>"
    + '<button type="button" class="ws-btn ws-addblock">＋ ' + esc(t("ws_add_block")) + "</button>"
    + '<span class="ws-sp"></span>'
    + '<button type="button" class="ws-btn ws-redesign">' + esc(t("studio_redesign")) + "</button>"
    + '<button type="button" class="ws-btn ws-pub">' + esc(t("studio_pub")) + "</button>"
    + '<a class="ws-btn ws-live" target="_blank" rel="noopener" style="display:none">' + esc(t("studio_open")) + "</a>"
    + "</div>"
    + '<div class="ws-modes"><button type="button" class="ws-m on" data-m="view">' + esc(t("studio_mode_view")) + "</button>"
    + '<button type="button" class="ws-m" data-m="text">' + esc(t("studio_mode_text")) + "</button>"
    + '<span class="ws-hint">' + esc(t("ws_pg_draftnote")) + "</span>"
    + '<span class="ws-sp"></span><button type="button" class="ws-m ws-askmaya">' + esc(t("studio_mode_maya")) + "</button></div>"
    + '<div class="ws-split"><aside class="ws-gallery">'
    + '<div class="ws-tabs"><button type="button" class="ws-tab on" data-tab="pages">' + esc(t("ws_tab_pages")) + "</button>"
    + '<button type="button" class="ws-tab" data-tab="design">' + esc(t("ws_tab_design")) + "</button></div>"
    + '<div class="ws-pane ws-ppane"><div class="ws-gh">' + esc(t("ws_pg_h")) + '</div><div class="ws-plist"></div><div class="ws-padd"></div>'
    + '<div class="ws-gh ws-blh">' + esc(t("ws_bl_h")) + '</div><div class="ws-blist"></div></div>'
    + '<div class="ws-pane ws-dpane" style="display:none"><div class="ws-gh">' + esc(t("ws_skin_h")) + '</div><div class="ws-gtpls"></div></div>'
    + "</aside>"
    + '<div class="ws-canvaswrap"><iframe class="ws-canvas" title="site"></iframe></div></div>'
    + '<div class="ws-blogv" hidden></div>'
    + '<div class="ws-msg" role="status" aria-live="polite"></div>';

  const sel = root.querySelector(".ws-page");
  const frame = root.querySelector(".ws-canvas");
  const wmsg = root.querySelector(".ws-msg");
  function say(m) { if (wmsg) wmsg.textContent = m || ""; }
  let BLOCKMETA = {};
  const META = { brand: "", theme_color: "", template: "", layout: "column", variant: "split" };
  const THEMES = { ids: [], templates: {}, families: [], "default": "" };
  let pendingEdit = null;   // {brand_id, token, blocks}
  let pendingInline = false;
  let pendingShown = null;
  let previewRecoveryToken = null;
  let previewRecoveryInFlight = false;

  // serialize ALL skin/colour/structure persists onto ONE chain (Codex fix 2)
  const _WQ = { pending: {}, running: false };
  function wsEnqueue(key, fn) {
    _WQ.pending[key] = fn;
    if (_WQ.running) return;
    _WQ.running = true;
    (function drain() {
      const ks = [];
      for (const k in _WQ.pending) ks.push(k);
      if (!ks.length) { _WQ.running = false; return; }
      const kk = ks[0];
      const f = _WQ.pending[kk];
      delete _WQ.pending[kk];
      Promise.resolve().then(f).then(drain, drain);
    })();
  }
  // per-key LAST CONFIRMED value (Codex fix 3)
  const LC = { skin: "", color: "", variant: "", layout: "" };
  let skinSeq = 0, colSeq = 0, strSeq = 0;

  const COLORPRESETS = [["#2847C4", "Indigo"], ["#0E8A6D", "Emerald"], ["#BA3B1D", "Vermilion"], ["#7A5FB0", "Violet"], ["#0F766E", "Teal"], ["#B7791F", "Amber"], ["#C2255C", "Rose"], ["#1F2937", "Charcoal"]];
  const VARIANTS = [["split", "ws_v_split"], ["centered", "ws_v_centered"], ["fullbleed", "ws_v_full"], ["textonly", "ws_v_text"]];
  const SHELLS = [["column", "ws_sh_col"], ["sidebar", "ws_sh_side"], ["landing", "ws_sh_land"]];
  function _isHex(v) { return /^#[0-9a-fA-F]{6}$/.test(v || ""); }
  function _hx2(v) { v = Math.round(Math.max(0, Math.min(255, v))); const s = v.toString(16); return s.length < 2 ? "0" + s : s; }
  function rgb2hex(a) { return "#" + _hx2(a[0] * 255) + _hx2(a[1] * 255) + _hx2(a[2] * 255); }
  function rgb2hls(r, g, b) {
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
    let h = 0, s = 0;
    if (mx !== mn) { const dd = mx - mn; s = l > 0.5 ? dd / (2 - mx - mn) : dd / (mx + mn); if (mx === r) h = (g - b) / dd + (g < b ? 6 : 0); else if (mx === g) h = (b - r) / dd + 2; else h = (r - g) / dd + 4; h /= 6; }
    return [h, l, s];
  }
  function _h2c(p, q, tt) {
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  }
  function hls2rgb(h, l, s) { if (s === 0) return [l, l, l]; const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q; return [_h2c(p, q, h + 1 / 3), _h2c(p, q, h), _h2c(p, q, h - 1 / 3)]; }
  function derivePaletteJS(hexv) {
    const r = parseInt(hexv.substr(1, 2), 16) / 255, g = parseInt(hexv.substr(3, 2), 16) / 255, b = parseInt(hexv.substr(5, 2), 16) / 255;
    const hls = rgb2hls(r, g, b), hh = hls[0], ll = hls[1], ss = hls[2];
    return { accent: hexv.toLowerCase(), strong: rgb2hex(hls2rgb(hh, Math.max(0.14, ll - 0.14), ss)), soft: rgb2hex(hls2rgb(hh, 0.95, Math.min(ss, 0.45))) };
  }
  function famName(f) {
    if (!f) return "";
    const m = f.name_i18n || {};
    return m[lang()] || f.name || f.id || "";
  }
  function applySkinLive() {
    try {
      const d = frame.contentDocument;
      if (!d || !d.documentElement) return;
      d.documentElement.setAttribute("data-theme", META.template || THEMES["default"] || "");
    } catch (_) { /* iframe mid-navigation */ }
  }
  function applyColorLive() {
    try {
      const d = frame.contentDocument;
      if (!d || !d.documentElement) return;
      const el = d.documentElement;
      if (!_isHex(META.theme_color)) { el.style.removeProperty("--accent"); el.style.removeProperty("--accent-strong"); el.style.removeProperty("--accent-soft"); return; }
      const p = derivePaletteJS(META.theme_color);
      el.style.setProperty("--accent", p.accent);
      el.style.setProperty("--accent-strong", p.strong);
      el.style.setProperty("--accent-soft", p.soft);
    } catch (_) { /* iframe mid-navigation */ }
  }
  let famOpen = "";
  function renderGallery() {
    const g = root.querySelector(".ws-gtpls");
    if (!g) return;
    const fams = THEMES.families || [];
    const cur = META.template || THEMES["default"];
    const curFam = (THEMES.templates[cur] || {}).family || "";
    if (!famOpen) famOpen = curFam || ((fams[0] || {}).id) || "";
    const openFam = famOpen;
    function themeCard(id) {
      const m = THEMES.templates[id] || {};
      return '<button type="button" class="ws-gt' + (id === cur ? " sel" : "") + '" data-tpl="' + esc(id) + '" title="' + esc(m.name || id) + '"><img class="ws-gthumb" loading="lazy" src="' + ctx.prefix() + "/api/theme-thumb/" + encodeURIComponent(id) + '" alt="' + esc(m.name || id) + '"><span class="ws-gn">' + esc(m.name || id) + '</span><span class="ws-gck">✓</span></button>';
    }
    if (!fams.length) {
      g.innerHTML = (THEMES.ids || []).map(themeCard).join("");
      g.querySelectorAll(".ws-gt").forEach((el) => { el.addEventListener("click", () => { pickSkin(el.getAttribute("data-tpl")); }); });
      return;
    }
    g.innerHTML = fams.map((f) => {
      const isOpen = f.id === openFam, isSel = f.id === curFam;
      let h = '<div class="ws-fam' + (isOpen ? " open" : "") + (isSel ? " sel" : "") + '"><button type="button" class="ws-famh" data-famh="' + esc(f.id) + '"><img class="ws-fthumb" loading="lazy" src="' + ctx.prefix() + "/api/theme-thumb/" + encodeURIComponent(f.rep || "") + '" alt="' + esc(famName(f)) + '"><span class="ws-fn">' + esc(famName(f)) + '</span><span class="ws-fck">✓</span></button>';
      if (isOpen) {
        h += '<div class="ws-fbody"><div class="ws-frow">' + (f.themes || []).map(themeCard).join("") + "</div>";
        h += '<div class="ws-slabel">' + esc(t("ws_color_h")) + '</div><div class="ws-colrow">'
          + COLORPRESETS.map((c) => '<button type="button" class="ws-cs' + (_isHex(META.theme_color) && META.theme_color.toLowerCase() === c[0].toLowerCase() ? " sel" : "") + '" title="' + esc(c[1]) + '" data-col="' + c[0] + '" style="background:' + c[0] + '"></button>').join("")
          + '<label class="ws-cpick" title="' + esc(t("ws_color_custom")) + '"><input type="color" class="ws-cinput" value="' + (_isHex(META.theme_color) ? esc(META.theme_color) : "#2847c4") + '"><span>+</span></label></div>';
        h += '<div class="ws-slabel">' + esc(t("ws_struct_h")) + '</div><div class="ws-strow">'
          + VARIANTS.map((v) => '<button type="button" class="ws-stbtn' + (v[0] === (META.variant || "split") ? " sel" : "") + '" data-variant="' + v[0] + '">' + esc(t(v[1])) + "</button>").join("") + "</div>"
          + '<div class="ws-strow">' + SHELLS.map((v) => '<button type="button" class="ws-stbtn' + (v[0] === (META.layout || "column") ? " sel" : "") + '" data-layout="' + v[0] + '">' + esc(t(v[1])) + "</button>").join("") + "</div>";
        h += "</div>";
      }
      h += "</div>";
      return h;
    }).join("");
    g.querySelectorAll("[data-famh]").forEach((el) => { el.addEventListener("click", () => { famOpen = el.getAttribute("data-famh"); renderGallery(); }); });
    g.querySelectorAll(".ws-gt").forEach((el) => { el.addEventListener("click", (ev) => { ev.stopPropagation(); pickSkin(el.getAttribute("data-tpl")); }); });
    g.querySelectorAll(".ws-cs").forEach((el) => { el.addEventListener("click", (ev) => { ev.stopPropagation(); pickColor(el.getAttribute("data-col")); }); });
    const ci = g.querySelector(".ws-cinput");
    if (ci) ci.addEventListener("input", () => { pickColor(ci.value); });
    g.querySelectorAll("[data-variant]").forEach((el) => { el.addEventListener("click", (ev) => { ev.stopPropagation(); pickStructure({ variant: el.getAttribute("data-variant") }); }); });
    g.querySelectorAll("[data-layout]").forEach((el) => { el.addEventListener("click", (ev) => { ev.stopPropagation(); pickStructure({ layout: el.getAttribute("data-layout") }); }); });
  }
  function pickColor(hexv) {
    if (!_isHex(hexv)) return;
    const prev = META.theme_color;
    if (hexv.toLowerCase() === (prev || "").toLowerCase()) return;
    META.theme_color = hexv;
    applyColorLive();
    renderGallery();
    const seq = (colSeq += 1);
    function revert() { META.theme_color = LC.color; applyColorLive(); renderGallery(); }
    wsEnqueue("color", () => ctx.api("/api/site/design", { method: "POST", json: { brand_id: brandId, theme_color: hexv } }).then((j) => {
      if (j && j.ok) LC.color = hexv;
      if (seq !== colSeq) return;
      if (j && (j.upgrade || j.error === "upgrade")) { revert(); ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (!(j && j.ok)) { revert(); ctx.toast(t("ws_skin_fail"), "", "warn"); return; }
      say(t("ws_color_done"));
    }).catch(() => { if (seq !== colSeq) return; revert(); ctx.toast(t("ws_skin_fail"), "", "warn"); }));
  }
  function pickStructure(chg) {
    if (chg.variant != null) { if (chg.variant === META.variant) return; META.variant = chg.variant; }
    if (chg.layout != null) { if (chg.layout === META.layout) return; META.layout = chg.layout; }
    renderGallery();
    const seq = (strSeq += 1);
    function revert() { META.variant = LC.variant; META.layout = LC.layout; renderGallery(); }
    wsEnqueue("struct", () => ctx.api("/api/site/layout", { method: "POST", json: { brand_id: brandId, variant: chg.variant, layout: chg.layout } }).then((j) => {
      if (j && j.ok) { if (chg.variant != null) LC.variant = chg.variant; if (chg.layout != null) LC.layout = chg.layout; }
      if (seq !== strSeq) return;
      if (j && (j.upgrade || j.error === "upgrade")) { revert(); ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (!(j && j.ok)) { revert(); ctx.toast(t("ws_skin_fail"), "", "warn"); return; }
      say(t("ws_struct_done"));
      loadCanvas(page);
    }).catch(() => { if (seq !== strSeq) return; revert(); ctx.toast(t("ws_skin_fail"), "", "warn"); }));
  }
  function pickSkin(id) {
    if (!id || id === META.template) return;
    const seq = (skinSeq += 1);
    META.template = id;
    renderGallery();
    applySkinLive();
    function revert() { META.template = LC.skin; renderGallery(); applySkinLive(); }
    wsEnqueue("skin", () => ctx.api("/api/site/design", { method: "POST", json: { brand_id: brandId, template: id } }).then((j) => {
      if (j && j.ok) LC.skin = id;
      if (seq !== skinSeq) return;
      if (j && (j.upgrade || j.error === "upgrade")) { revert(); ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (!(j && j.ok)) { revert(); ctx.toast(t("ws_skin_fail"), "", "warn"); return; }
      say(t("ws_skin_done"));
      loadCanvas(page);
    }).catch(() => { if (seq !== skinSeq) return; revert(); ctx.toast(t("ws_skin_fail"), "", "warn"); }));
  }
  // BUG #5: while a hero image is still generating the draft renders a placeholder — refresh every
  // 15s up to 12 times until the marker is gone.
  let _heroT = null, _heroN = 0, _heroAuto = false;
  function wsHeroWatch(fd) {
    if (_heroT) { clearTimeout(_heroT); _heroT = null; }
    let gen = false;
    try { gen = !!(fd && fd.querySelector && fd.querySelector('[data-hero-state="generating"]')); } catch (_) { gen = false; }
    if (!gen) { _heroN = 0; return; }
    if (_heroN >= 12) return;
    _heroN++;
    _heroT = setTimeout(() => {
      _heroT = null;
      if (!alive) return;
      _heroAuto = true;
      loadCanvas(page);
    }, 15000);
  }
  function wsImageAction(fd) {
    try {
      if (!fd || !fd.body) return;
      if (!fd.getElementById("soa-wb-image-action-style")) {
        const st = fd.createElement("style");
        st.id = "soa-wb-image-action-style";
        st.textContent = '.soa-wb-reimage-host{position:relative!important}.soa-wb-reimage{all:initial!important;box-sizing:border-box!important;position:absolute!important;z-index:2147482600!important;top:10px!important;right:10px!important;width:36px!important;height:36px!important;display:flex!important;align-items:center!important;justify-content:center!important;border:1px solid rgba(18,24,40,.22)!important;border-radius:6px!important;background:rgba(255,255,255,.96)!important;color:#2847C4!important;box-shadow:0 3px 12px rgba(0,0,0,.18)!important;font:700 21px/1 system-ui,-apple-system,sans-serif!important;letter-spacing:0!important;cursor:pointer!important}'
          + ".soa-wb-reimage:hover{background:#fff!important;color:#BA3B1D!important}"
          + ".soa-wb-reimage:focus-visible{outline:3px solid #2847C4!important;outline-offset:2px!important}";
        fd.head.appendChild(st);
      }
      fd.querySelectorAll('[data-block-type="hero_visual"],[data-soa-block-type="hero_visual"]').forEach((rt) => {
        if (rt.querySelector(".soa-wb-reimage") || rt.querySelector('[data-hero-state="generating"]')) return;
        const img = rt.querySelector("img.hv-shot");
        const svg = rt.querySelector("figure svg, .hv-visual figure svg");
        const media = img && img.getAttribute("src") ? img : svg;
        if (!media) return;
        let host = (img ? img.closest(".hv-photo") : svg.closest("figure")) || media.parentElement || rt;
        if (host.closest && host.closest('[aria-hidden="true"]')) host = rt;
        if (host.classList) host.classList.add("soa-wb-reimage-host");
        const btn = fd.createElement("button");
        btn.type = "button";
        btn.className = "soa-wb-reimage";
        btn.textContent = "↻";
        btn.setAttribute("aria-label", t("ws_reimage"));
        btn.setAttribute("title", t("ws_reimage"));
        btn.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); wsSay(t("ws_say_reimage")); });
        host.appendChild(btn);
      });
    } catch (_) { /* best-effort chrome */ }
  }
  function wsPendingEditLive() { return !!(pendingEdit && String(pendingEdit.brand_id) === String(brandId)); }
  function wsPendingPage() {
    const p = pendingEdit;
    const blocks = p && Array.isArray(p.blocks) ? p.blocks : [];
    for (let i = 0; i < blocks.length; i++) {
      const pg = String((blocks[i] && blocks[i].page) || "").trim();
      if (pg && PAGES.some((x) => x.slug === pg)) return pg;
    }
    return "";
  }
  function wsPendingPageLabel(pg) {
    const p = PAGES.filter((x) => x.slug === pg)[0];
    return p ? pgName(p) : pg;
  }
  function wsRevealPending() {
    if (!wsPendingEditLive()) return "";
    const pg = wsPendingPage();
    const cur = String(page || "home");
    if (pg && pg !== cur) { page = pg; if (sel) sel.value = pg; }
    const msg = pg ? wsFill("se_previewing_page", { p: wsPendingPageLabel(pg) }) : t("se_previewing");
    say(msg);
    return pg || cur;
  }
  function wsPaintPending(fd) {
    const p = pendingEdit;
    if (!p || String(p.brand_id) !== String(brandId)) return;
    fd = fd || frame.contentDocument;
    if (!fd || !fd.body) return;
    const blocks = p.blocks || [];
    const cur = page || "home";
    if (!fd.getElementById("soa-se-style")) {
      const st = fd.createElement("style");
      st.id = "soa-se-style";
      st.textContent = '[data-soa-siteedit-pending]{position:relative!important;outline:3px solid #d97706!important;outline-offset:4px!important}'
        + '[data-soa-siteedit-pending="add"]{outline-color:#2563eb!important}'
        + '[data-soa-siteedit-pending="remove"]{opacity:.58}'
        + '.soa-se-badge{position:absolute!important;z-index:2147482500!important;top:auto!important;right:-4px!important;bottom:calc(100% + 6px)!important;max-width:calc(100% - 8px)!important;display:flex!important;align-items:center!important;flex-wrap:wrap!important;gap:6px!important;padding:5px 6px 5px 8px!important;border-radius:5px!important;background:#fff7ed!important;color:#9a3412!important;border:1px solid #fdba74!important;box-shadow:0 3px 12px rgba(0,0,0,.14)!important;font:700 12px/1.35 system-ui,-apple-system,sans-serif!important;letter-spacing:0!important;pointer-events:auto!important}'
        + ".soa-se-actions{display:flex!important;align-items:center!important;gap:4px!important}.soa-se-actions button{all:unset!important;box-sizing:border-box!important;min-height:28px!important;padding:6px 8px!important;border:1px solid #d97706!important;border-radius:4px!important;background:#fff!important;color:#9a3412!important;font:700 11px/1 system-ui,-apple-system,sans-serif!important;letter-spacing:0!important;cursor:pointer!important}.soa-se-actions button:first-child{background:#9a3412!important;color:#fff!important}.soa-se-actions button:disabled{opacity:.6!important;cursor:wait!important}.soa-se-actions button:focus-visible{outline:3px solid #2847C4!important;outline-offset:2px!important}";
      fd.head.appendChild(st);
    }
    const roots = [].slice.call(fd.querySelectorAll("[data-block-id],[data-soa-block-id]"));
    let focus = null;
    blocks.forEach((bl) => {
      if (!bl || String(bl.page || "home") !== String(cur)) return;
      let rt = null;
      for (let i = 0; i < roots.length; i++) {
        const id = roots[i].getAttribute("data-block-id") || roots[i].getAttribute("data-soa-block-id");
        if (String(id) === String(bl.block_id)) { rt = roots[i]; break; }
      }
      if (!rt) return;
      rt.querySelectorAll(".soa-se-badge").forEach((x) => x.remove());
      rt.removeAttribute("data-soa-siteedit-pending");
      rt.querySelectorAll("[data-soa-siteedit-pending]").forEach((x) => x.removeAttribute("data-soa-siteedit-pending"));
      rt.setAttribute("data-soa-siteedit-pending", !bl.before_block && bl.after_block ? "add" : (bl.after_block ? "1" : "remove"));
      if (!focus) focus = rt;
      const badge = fd.createElement("div");
      badge.className = "soa-se-badge";
      const label = fd.createElement("span");
      label.className = "soa-se-label";
      label.textContent = t("se_pending_block");
      badge.appendChild(label);
      const acts = fd.createElement("div");
      acts.className = "mc-acts soa-se-actions";
      [["apply", "se_apply"], ["discard", "se_discard"]].forEach((spec) => {
        const btn = fd.createElement("button");
        btn.type = "button";
        btn.textContent = t(spec[1]);
        btn.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); seBadgeAction(btn, spec[0], p.token); });
        acts.appendChild(btn);
      });
      badge.appendChild(acts);
      rt.appendChild(badge);
    });
    pendingInline = !!focus;
    if (focus) wsFocusBlock(focus);
    say(t("se_previewing"));
  }
  function seBadgeAction(btn, action, token) {
    btn.disabled = true;
    ctx.api("/api/site/edit/" + action, { method: "POST", json: { brand_id: brandId, token } }).then((j) => {
      if (action === "discard") {
        clearPending(brandId, token);
        if (opts.onPendingResolved) opts.onPendingResolved(token, "discarded");
        return;
      }
      if (j && j.ok && j.applied) {
        clearPending(brandId, token, true);
        ctx.toast(t("se_applied"), "", "ok");
        if (opts.onPendingResolved) opts.onPendingResolved(token, "applied");
        syncDraftMeta();
        return;
      }
      if (j && (j.drift || j.stale)) {
        clearPending(brandId, token);
        if (opts.onPendingResolved) opts.onPendingResolved(token, "stale");
        ctx.toast(t("se_stale"), "", "warn");
        return;
      }
      if (j && j.gone) {
        clearPending(brandId, token);
        if (opts.onPendingResolved) opts.onPendingResolved(token, "gone");
        return;
      }
      btn.disabled = false;
    }).catch(() => { btn.disabled = false; ctx.toast(t("se_fail"), "", "warn"); });
  }
  function wsClearPendingPaint(fd) {
    fd = fd || frame.contentDocument;
    if (!fd || !fd.body) return;
    fd.querySelectorAll(".soa-se-badge").forEach((x) => x.remove());
    fd.querySelectorAll("[data-soa-siteedit-pending]").forEach((x) => x.removeAttribute("data-soa-siteedit-pending"));
    say(mode === "text" ? t("ws_text_hint") : t("ws_view_hint"));
  }

  // ── PAGES + SECTIONS ── every button below SPEAKS a sentence to Maya instead of calling an
  // endpoint itself — the command surface is the conversation.
  const WS_NAV_VIA_CHAT = false;
  let PAGES = [];
  const PUB = { on: false, set: {} };
  const PGS = { ren: "", loaded: false, badd: false };
  const BTYPES = [
    ["prose", "ws_bt_prose", ["paragraph", "段落", "段落"]],
    ["faq", "ws_bt_faq", ["FAQ", "常见问题", "よくある質問"]],
    ["compare", "ws_bt_compare", ["comparison", "对比", "比較"]],
    ["data_facts", "ws_bt_data", ["key facts", "数据", "データ"]],
    ["entity", "ws_bt_entity", ["entity", "实体", "エンティティ"]],
    ["howto", "ws_bt_howto", ["steps", "步骤", "手順"]],
    ["pricing", "ws_bt_pricing", ["pricing", "价格", "料金"]],
    ["testimonials", "ws_bt_reviews", ["reviews", "评价", "お客様の声"]],
  ];
  function wsLangIdx() { return lang() === "zh" ? 1 : (lang() === "ja" ? 2 : 0); }
  function wsFill(key, vals) {
    let s = t(key);
    for (const k in vals || {}) s = s.split("{" + k + "}").join(vals[k]);
    return s;
  }
  function wsBlockLabel(b) {
    if (!b) return "";
    const ks = ["heading", "tagline", "brand", "definition"];
    for (let i = 0; i < ks.length; i++) {
      const v = b[ks[i]];
      if (typeof v === "string" && v.trim()) return v.trim().slice(0, 60);
    }
    return b.type || "";
  }
  function wsSay(sentence, siteContext) {
    if (!sentence) return;
    if (META.brand && opts.selectBrandByName) opts.selectBrandByName(META.brand);
    say(t("ws_say_sent"));
    if (opts.ask) opts.ask(sentence, siteContext || null);
  }
  function wsBlockContext(el, rt, snip) {
    const block = rt || (el && el.closest ? el.closest("[data-block-id],[data-soa-block-id]") : null);
    if (!block) return null;
    const pageRoot = block.getAttribute && block.getAttribute("data-soa-page-id") != null ? block : null;
    const id = block.getAttribute("data-block-id") || block.getAttribute("data-soa-block-id") || (pageRoot ? "__page__" : "");
    if (!id) return null;
    const meta = BLOCKMETA[id] || {};
    const pg = PAGES.filter((p) => p.slug === (page || "home"))[0] || {};
    const node = el && el.closest ? el.closest("[data-soa-node-id]") : null;
    return {
      page: page || "home",
      page_id: pg.page_id || null,
      page_version: pg.draft_version || null,
      block_id: id,
      block_type: pageRoot ? "page" : (block.getAttribute("data-block-type") || block.getAttribute("data-soa-block-type") || meta.type || ""),
      node_id: node ? node.getAttribute("data-soa-node-id") : (pageRoot ? "page-h1" : id),
      field: (el && el.getAttribute ? (el.getAttribute("data-field") || el.getAttribute("data-soa-page-field")) : null) || (pageRoot ? "title" : null),
      expected_text: el && el.innerText ? el.innerText.trim() : "",
      selection: (snip || "").slice(0, 240),
    };
  }
  function pgName(p) { return p.nav_label || p.title || p.slug; }
  function pgGo(s) {
    if (!s) return;
    if (WS_NAV_VIA_CHAT) { wsSay(wsFill("ws_say_showpage", { p: s })); return; }
    if (sel.value !== s) sel.value = s;
    loadCanvas(s);
  }
  function pgDelSay(s) { if (s) wsSay(wsFill("ws_say_delpage", { p: s })); }
  function pgRenameSay(s, val) {
    const v = (val || "").trim();
    PGS.ren = "";
    renderPageList();
    if (!s || !v) return;
    wsSay(wsFill("ws_say_renpage", { p: s, n: v }));
  }
  function renderPageList() {
    const el = root.querySelector(".ws-plist");
    if (!el) return;
    if (!PGS.loaded) return;
    if (!PAGES.length) { el.innerHTML = '<div class="ws-pnote">' + esc(t("ws_pg_nopages")) + "</div>"; renderAdd(); renderBlockList(); return; }
    const cur = page || "home";
    el.innerHTML = PAGES.map((p) => {
      const s = p.slug;
      const home = s === "home";
      let bd = "";
      if (PUB.on) bd += '<span class="ws-pb ' + (PUB.set[s] ? "live" : "dft") + '">' + esc(PUB.set[s] ? t("ws_pg_live") : t("ws_pg_draftonly")) + "</span>";
      if (p.nav_hidden) bd += '<span class="ws-pb">' + esc(t("ws_pg_hidden")) + "</span>";
      const nm = PGS.ren === s
        ? '<input class="ws-pin" data-pren-in="' + esc(s) + '" value="' + esc(pgName(p)) + '" maxlength="40">'
        : '<div class="ws-pnm">' + esc(pgName(p)) + "</div>";
      return '<div class="ws-prow' + (s === cur ? " cur" : "") + '" data-pg="' + esc(s) + '">' + nm
        + '<div class="ws-pmeta">/' + esc(s) + " · " + (p.blocks || 0) + " " + esc(t("ws_pg_blk")) + "</div>"
        + (bd ? '<div class="ws-pbadges">' + bd + "</div>" : "")
        + (PGS.ren === s ? '<div class="ws-pnote">' + esc(t("ws_pg_rennote")) + "</div>" : "")
        + '<div class="ws-pacts"><button type="button" class="ws-pact" data-pgo="' + esc(s) + '">' + esc(t("ws_pg_edit")) + "</button>"
        + (s === "blog" ? '<button type="button" class="ws-pact" data-bvopen="1">' + esc(t("ws_bv_manage")) + "</button>" : "")
        + '<button type="button" class="ws-pact" data-pren="' + esc(s) + '">' + esc(PGS.ren === s ? t("ws_pg_save") : t("ws_pg_rename")) + "</button>"
        + '<button type="button" class="ws-pact del" data-pdel="' + esc(s) + '"' + (home ? ' disabled title="' + esc(t("ws_pgx_home")) + '"' : "") + ">" + esc(t("ws_pg_del")) + "</button></div>"
        + "</div>";
    }).join("");
    el.querySelectorAll(".ws-prow").forEach((r) => {
      r.addEventListener("click", (ev) => {
        if (ev.target.closest("button") || ev.target.closest("input")) return;
        pgGo(r.getAttribute("data-pg"));
      });
    });
    el.querySelectorAll("[data-pgo]").forEach((b) => { b.addEventListener("click", () => { pgGo(b.getAttribute("data-pgo")); }); });
    el.querySelectorAll("[data-bvopen]").forEach((b) => { b.addEventListener("click", () => { bvOpen(); }); });
    el.querySelectorAll("[data-pren]").forEach((b) => {
      b.addEventListener("click", () => {
        const s = b.getAttribute("data-pren");
        if (PGS.ren === s) {
          const inp = el.querySelector("[data-pren-in]");
          pgRenameSay(s, inp ? inp.value : "");
        } else {
          PGS.ren = s;
          renderPageList();
          const i2 = el.querySelector("[data-pren-in]");
          if (i2) { try { i2.focus(); i2.select(); } catch (_) { /* detached */ } }
        }
      });
    });
    el.querySelectorAll("[data-pren-in]").forEach((i) => {
      i.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") { ev.preventDefault(); pgRenameSay(i.getAttribute("data-pren-in"), i.value); } else if (ev.key === "Escape") { PGS.ren = ""; renderPageList(); }
      });
    });
    el.querySelectorAll("[data-pdel]").forEach((b) => { b.addEventListener("click", () => { pgDelSay(b.getAttribute("data-pdel")); }); });
    renderAdd();
    renderBlockList();
  }
  function renderAdd() {
    const el = root.querySelector(".ws-padd");
    if (!el) return;
    el.innerHTML = '<div class="ws-pnote">' + esc(t("ws_pg_draftnote")) + "</div>";
  }
  function blCur() {
    const c = page || "home";
    const m = PAGES.filter((p) => p.slug === c);
    return m.length ? m[0] : null;
  }
  function renderBlockList() {
    const el = root.querySelector(".ws-blist");
    if (!el) return;
    const hd = root.querySelector(".ws-blh");
    const p = blCur();
    if (hd) hd.textContent = wsFill("ws_bl_h", { p: p ? pgName(p) : "" });
    if (!p) { el.innerHTML = ""; return; }
    const bl = p.bl || [];
    let h = "";
    if (!bl.length) h += '<div class="ws-pnote">' + esc(t("ws_bl_none")) + "</div>";
    h += bl.map((b, i) => (
      '<div class="ws-brow"><div class="ws-bnm">' + esc(b.label || b.type) + "</div>"
      + '<div class="ws-bmeta">' + esc(b.type) + "</div>"
      + '<div class="ws-pacts">'
      + '<button type="button" class="ws-pact" data-bup="' + i + '"' + (i === 0 ? " disabled" : "") + ' title="' + esc(t("ws_bl_up")) + '">↑</button>'
      + '<button type="button" class="ws-pact" data-bdn="' + i + '"' + (i === bl.length - 1 ? " disabled" : "") + ' title="' + esc(t("ws_bl_dn")) + '">↓</button>'
      + '<button type="button" class="ws-pact del" data-bdel="' + i + '">' + esc(t("ws_pg_del")) + "</button>"
      + "</div></div>"
    )).join("");
    h += '<div class="ws-badd">';
    if (!PGS.badd) h += '<button type="button" class="ws-pnew ws-bnew">＋ ' + esc(t("ws_add_block")) + "</button>";
    else h += '<div class="ws-bpick"><div class="ws-plab">' + esc(t("ws_bl_pick")) + "</div>"
      + BTYPES.map((x) => '<button type="button" class="ws-pact" data-bnew="' + esc(x[0]) + '">' + esc(t(x[1])) + "</button>").join("")
      + '<button type="button" class="ws-pact ws-bno">' + esc(t("ws_pg_cancel")) + "</button></div>";
    h += '<div class="ws-pnote">' + esc(t("ws_bl_note")) + "</div></div>";
    el.innerHTML = h;
    const pslug = p.slug;
    function lbl(i) { return (bl[i] && (bl[i].label || bl[i].type)) || ""; }
    el.querySelectorAll("[data-bup]").forEach((b) => {
      b.addEventListener("click", () => { wsSay(wsFill("ws_say_upblock", { p: pslug, n: lbl(parseInt(b.getAttribute("data-bup"), 10)) })); });
    });
    el.querySelectorAll("[data-bdn]").forEach((b) => {
      b.addEventListener("click", () => { wsSay(wsFill("ws_say_dnblock", { p: pslug, n: lbl(parseInt(b.getAttribute("data-bdn"), 10)) })); });
    });
    el.querySelectorAll("[data-bdel]").forEach((b) => {
      b.addEventListener("click", () => { wsSay(wsFill("ws_say_delblock", { p: pslug, n: lbl(parseInt(b.getAttribute("data-bdel"), 10)) })); });
    });
    const nb = el.querySelector(".ws-bnew");
    if (nb) nb.addEventListener("click", () => { PGS.badd = true; renderBlockList(); });
    const no = el.querySelector(".ws-bno");
    if (no) no.addEventListener("click", () => { PGS.badd = false; renderBlockList(); });
    el.querySelectorAll("[data-bnew]").forEach((b) => {
      b.addEventListener("click", () => {
        const ty = b.getAttribute("data-bnew");
        PGS.badd = false;
        renderBlockList();
        let w = null;
        BTYPES.forEach((x) => { if (x[0] === ty) w = x[2][wsLangIdx()]; });
        wsSay(wsFill("ws_say_addblock", { p: pslug, b: w || ty }));
      });
    });
  }

  // ── P2b BLOG VIEW ── the article list lives in the SAME drawer: studio panes hidden, not unmounted.
  const BV = { data: null, art: null, conf: null, busy: false };
  function bvEl() { return root.querySelector(".ws-blogv"); }
  function bvShow(on) {
    const v = bvEl();
    if (!v) return;
    root.classList.toggle("ws-blogmode", !!on);
    v.hidden = !on;
    if (on) v.scrollTop = 0;
  }
  function bvOpen() { BV.art = null; BV.conf = null; bvShow(true); bvRender(); bvLoad(); }
  function bvBack() { BV.art = null; BV.conf = null; bvShow(false); renderPageList(); }
  function bvLoad() {
    return ctx.api("/api/site/articles?brand_id=" + encodeURIComponent(brandId)).then((j) => {
      if (!alive) return;
      BV.data = j && j.ok ? j : { articles: [] };
      bvRender();
    }).catch(() => { BV.data = { articles: [] }; bvRender(); });
  }
  function bvCrumb(inner, back) {
    return '<div class="ws-crumb"><button type="button" class="ws-cbtn" data-bvback="' + back + '">'
      + esc(back === "list" ? t("ws_bv_backlist") : t("ws_bv_back")) + '</button><span class="ws-ch">' + esc(inner) + "</span></div>";
  }
  function bvBadges(a) {
    let h = '<span class="ws-pb ' + (a.status === "published" ? "live" : "dft") + '">' + esc(a.status === "published" ? t("ws_bv_live") : t("ws_bv_draft")) + "</span>";
    if (a.has_cover) h += '<span class="ws-pb">' + esc(t("ws_bv_cover")) + "</span>";
    h += '<span class="ws-pb">' + esc(a.kind === "deliverable" ? t("ws_bv_kind_d") : t("ws_bv_kind_p")) + "</span>";
    if (a.kind === "deliverable" && !a.on_site) h += '<span class="ws-pb">' + esc(t("ws_bv_offsite")) + "</span>";
    return h;
  }
  function bvConfHtml(c) {
    const noteKey = c.act === "del" ? (c.pub ? "ws_bv_delnote" : "ws_bv_delnote_draft") : "ws_bv_unpubnote";
    return '<div class="ws-pconf"><b>' + esc(t(c.act === "del" ? "ws_bv_delq" : "ws_bv_unpubq")) + "</b>"
      + '<div class="ws-pnote">' + esc(t(noteKey)) + "</div>"
      + '<div class="ws-aacts"><button type="button" class="ws-pact del" data-bvok="' + esc(c.slug) + '" data-bvact="' + esc(c.act) + '">'
      + esc(t(c.act === "del" ? "ws_pg_del" : "ws_bv_unpub")) + "</button>"
      + '<button type="button" class="ws-pact" data-bvno="1">' + esc(t("ws_pg_cancel")) + "</button></div></div>";
  }
  function bvRow(a) {
    let acts = "";
    if (a.kind === "deliverable") {
      acts += '<button type="button" class="ws-pact" data-bvview="' + esc(a.slug) + '">' + esc(t("ws_bv_view")) + "</button>";
      acts += '<button type="button" class="ws-pact" data-bvcov="' + esc(a.slug) + '">' + esc(t("ws_bv_cov")) + "</button>";
      if (a.status === "published") acts += '<button type="button" class="ws-pact" data-bvunpub="' + esc(a.slug) + '">' + esc(t("ws_bv_unpub")) + "</button>";
      acts += '<button type="button" class="ws-pact del" data-bvdel="' + esc(a.slug) + '" data-bvpub="' + (a.status === "published" ? 1 : 0) + '">' + esc(t("ws_pg_del")) + "</button>";
    } else {
      acts += '<button type="button" class="ws-pact" data-bvedit="' + esc(a.page_slug) + '">' + esc(t("ws_bv_edit")) + "</button>";
      acts += '<button type="button" class="ws-pact del" data-bvpdel="' + esc(a.page_slug) + '">' + esc(t("ws_pg_del")) + "</button>";
    }
    const cf = BV.conf && BV.conf.slug === a.slug ? bvConfHtml(BV.conf) : "";
    return '<div class="ws-arow"><div class="ws-atitle">' + esc(a.title || a.slug) + "</div>"
      + '<div class="ws-ameta">/' + esc(a.page_slug || a.slug) + "</div>"
      + '<div class="ws-abadges">' + bvBadges(a) + "</div>"
      + '<div class="ws-aacts">' + acts + "</div>" + cf + "</div>";
  }
  function bvRender() {
    const v = bvEl();
    if (!v) return;
    if (BV.art) { bvRenderArt(v); return; }
    const arr = BV.data && BV.data.articles ? BV.data.articles : null;
    let h = bvCrumb(t("ws_bv_h"), "pages") + '<div class="ws-bnote">' + esc(t("ws_bv_immediate")) + "</div>";
    h += '<button type="button" class="ws-pnew" data-bvnew="1">' + esc(t("blog_new")) + "</button>";
    if (arr === null) h += '<div class="ws-pnote">' + esc(t("wb_loading")) + "</div>";
    else if (!arr.length) h += '<div class="ws-pnote">' + esc(t("ws_bv_empty")) + "</div>";
    else {
      const mine = arr.filter((a) => a.kind === "deliverable");
      const gen = arr.filter((a) => a.kind === "page");
      if (mine.length) h += '<div class="ws-agroup">' + esc(t("ws_bv_group_d")) + " · " + mine.length + "</div>" + mine.map(bvRow).join("");
      if (gen.length) h += '<div class="ws-agroup">' + esc(t("ws_bv_group_p")) + " · " + gen.length + "</div>" + gen.map(bvRow).join("");
    }
    v.innerHTML = h;
    bvBind(v);
  }
  function bvRenderArt(v) {
    const d = BV.art;
    let h = bvCrumb(d.title || "", "list");
    if (!d.slug) { v.innerHTML = h + '<div class="ws-pnote">' + esc(t("wb_notfound")) + "</div>"; bvBind(v); return; }
    h += '<div class="ws-bnote">' + esc(d.published ? t("ws_bv_immediate") : t("ws_bv_pubnote")) + "</div>";
    h += '<div class="ws-adoc">' + mdLite(d.body || "", {}, { t }) + "</div>";
    h += '<div class="ws-abar">';
    if (d.published) {
      if (d.live_url) h += '<a class="ws-pact" href="' + esc(d.live_url) + '" target="_blank" rel="noopener">' + esc(t("art_view_live")) + "</a>";
      h += '<button type="button" class="mc-cta art-publish">' + esc(t("art_republish")) + "</button>";
      h += '<button type="button" class="ws-pact" data-bvunpub="' + esc(d.slug) + '">' + esc(t("ws_bv_unpub")) + "</button>";
    } else h += '<button type="button" class="mc-cta art-publish">' + esc(t("art_publish")) + "</button>";
    h += '<button type="button" class="ws-pact" data-bvcov="' + esc(d.slug) + '">' + esc(t("ws_bv_cov")) + "</button>";
    h += '<button type="button" class="ws-pact del" data-bvdel="' + esc(d.slug) + '" data-bvpub="' + (d.published ? 1 : 0) + '">' + esc(t("ws_pg_del")) + "</button>";
    h += '<div class="art-publine" style="display:none"></div></div>';
    if (BV.conf && BV.conf.slug === d.slug) h += bvConfHtml(BV.conf);
    v.innerHTML = h;
    bvBind(v);
    const bar = v.querySelector(".ws-abar");
    if (bar && bar.querySelector(".art-publish")) {
      bar.querySelector(".art-publish").addEventListener("click", () => {
        articlePublish(d, bar, () => { d.published = true; d.live_url = d.live_url || null; bvRender(); bvLoad(); }, ctx);
      });
    }
    v.querySelectorAll("[data-mdeliv]").forEach((b) => {
      b.addEventListener("click", () => { if (opts.openDeliverable) opts.openDeliverable(b.getAttribute("data-mdeliv")); });
    });
  }
  function bvBind(v) {
    v.querySelectorAll("[data-bvback]").forEach((b) => {
      b.addEventListener("click", () => {
        if (b.getAttribute("data-bvback") === "list") { BV.art = null; BV.conf = null; bvRender(); } else bvBack();
      });
    });
    v.querySelectorAll("[data-bvview]").forEach((b) => { b.addEventListener("click", () => { bvArt(b.getAttribute("data-bvview")); }); });
    v.querySelectorAll("[data-bvedit]").forEach((b) => { const s = b.getAttribute("data-bvedit"); bvBack(); pgGo(s); });
    v.querySelectorAll("[data-bvpdel]").forEach((b) => {
      const s = b.getAttribute("data-bvpdel");
      ctx.toast(t("ws_bv_pagedel"), "/" + s, "info");
      bvBack();
      pgDelSay(s);
    });
    v.querySelectorAll("[data-bvdel]").forEach((b) => {
      b.addEventListener("click", () => { BV.conf = { slug: b.getAttribute("data-bvdel"), act: "del", pub: b.getAttribute("data-bvpub") === "1" }; bvRender(); });
    });
    v.querySelectorAll("[data-bvunpub]").forEach((b) => {
      b.addEventListener("click", () => { BV.conf = { slug: b.getAttribute("data-bvunpub"), act: "unpub" }; bvRender(); });
    });
    v.querySelectorAll("[data-bvno]").forEach((b) => { b.addEventListener("click", () => { BV.conf = null; bvRender(); }); });
    v.querySelectorAll("[data-bvok]").forEach((b) => {
      b.addEventListener("click", () => {
        const s = b.getAttribute("data-bvok");
        if (b.getAttribute("data-bvact") === "del") bvDelete(s); else bvUnpublish(s);
      });
    });
    v.querySelectorAll("[data-bvcov]").forEach((b) => { b.addEventListener("click", () => { bvCover(b.getAttribute("data-bvcov")); }); });
    v.querySelectorAll("[data-bvnew]").forEach((b) => { b.addEventListener("click", () => { if (opts.newBlog) opts.newBlog(META.brand || ""); }); });
  }
  function bvArt(slug) {
    if (!slug) return;
    BV.conf = null;
    BV.art = { title: t("wb_loading"), slug: "", body: "" };
    bvRender();
    ctx.api("/api/deliverables/" + encodeURIComponent(slug)).then((d) => {
      if (!alive) return;
      BV.art = d && d.slug ? d : { title: "", slug: "", body: "" };
      bvRender();
    }).catch(() => { BV.art = { title: "", slug: "", body: "" }; bvRender(); });
  }
  function bvTool(path, payload) {
    const b = { brand_id: brandId, silent: true };
    Object.assign(b, payload || {});
    return ctx.api(path, { method: "POST", json: b });
  }
  function bvDelete(slug) {
    if (BV.busy) return;
    BV.busy = true;
    wsEnqueue("art:del:" + slug, () => bvTool("/api/tool/delete-article", { slug, confirm: true }).then((j) => {
      BV.busy = false;
      if (j && (j.upgrade || j.error === "upgrade")) { ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (!(j && j.ok)) { ctx.toast((j && (j.message || j.error)) || t("ws_pg_fail"), "", "warn"); BV.conf = null; bvRender(); return; }
      ctx.toast(t("ws_bv_deleted"), "", "ok");
      BV.conf = null;
      BV.art = null;
      return bvLoad().then(() => loadDraft(false, null));
    }).catch(() => { BV.busy = false; ctx.toast(t("ws_pg_fail"), "", "warn"); }));
  }
  function bvUnpublish(slug) {
    if (BV.busy) return;
    BV.busy = true;
    wsEnqueue("art:unpub:" + slug, () => ctx.api("/api/site/article/unpublish", { method: "POST", json: { brand_id: brandId, slug } }).then((j) => {
      BV.busy = false;
      if (j && (j.upgrade || j.error === "upgrade")) { ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (!(j && j.ok)) { ctx.toast((j && j.msg) || t("ws_pg_fail"), "", "warn"); BV.conf = null; bvRender(); return; }
      if (j.meta_cleared === false) ctx.toast(j.warning || t("ws_bv_unpub_partial"), "", "warn");
      else ctx.toast(t("ws_bv_unpubbed"), "", "ok");
      BV.conf = null;
      if (BV.art && BV.art.slug === slug) { BV.art.published = j.meta_cleared === false; BV.art.live_url = null; }
      return bvLoad().then(() => loadDraft(false, null));
    }).catch(() => { BV.busy = false; ctx.toast(t("ws_pg_fail"), "", "warn"); }));
  }
  function bvCover(slug) {
    if (BV.busy) return;
    BV.busy = true;
    wsEnqueue("art:cov:" + slug, () => bvTool("/api/tool/set-article-cover", { slug }).then((j) => {
      BV.busy = false;
      if (j && (j.upgrade || j.error === "upgrade")) { ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (!(j && j.ok)) { ctx.toast((j && (j.message || j.error)) || t("ws_pg_fail"), "", "warn"); return; }
      ctx.toast(t("ws_bv_covgen"), "", "info");
    }).catch(() => { BV.busy = false; ctx.toast(t("ws_pg_fail"), "", "warn"); }));
  }
  function loadStatus() {
    return ctx.api("/api/site/status?brand_id=" + encodeURIComponent(brandId)).then((s) => {
      if (!alive || !s) return;
      PUB.on = !!s.published_at;
      PUB.set = {};
      (s.pages_published || []).forEach((x) => { PUB.set[x] = 1; });
      renderPageList();
    }).catch(() => { /* status is cosmetic */ });
  }
  function applyDraft(doc) {
    META.brand = doc.brand || "";
    META.theme_color = doc.theme_color || "";
    META.template = doc.template || "";
    META.layout = doc.layout || "column";
    META.variant = "split";
    const currentVersions = {};
    PAGES.forEach((p) => { if (p.page_id != null && p.draft_version != null) currentVersions[String(p.page_id)] = Number(p.draft_version); });
    BLOCKMETA = {};
    PAGES = [];
    sel.innerHTML = "";
    (doc.pages || []).forEach((p) => {
      const op = document.createElement("option");
      op.value = p.slug;
      op.textContent = p.nav_label || p.title || p.slug;
      sel.appendChild(op);
      let draftVersion = p.draft_version == null ? null : Number(p.draft_version);
      const knownVersion = currentVersions[String(p.page_id)];
      if (knownVersion != null) draftVersion = Math.max(draftVersion == null ? knownVersion : draftVersion, knownVersion);
      PAGES.push({
        slug: p.slug, page_id: p.page_id || null, draft_version: draftVersion, title: p.title || "", nav_label: p.nav_label || "", nav_hidden: !!p.nav_hidden, blocks: (p.blocks || []).length,
        bl: (p.blocks || []).map((bb) => ({ id: (bb && bb.id) || "", type: (bb && bb.type) || "", label: wsBlockLabel(bb) })),
      });
      (p.blocks || []).forEach((bl) => {
        if (bl && bl.id) BLOCKMETA[bl.id] = { page: p.slug, type: bl.type || "", label: wsBlockLabel(bl) };
        if (bl && bl.type === "hero_visual" && bl.variant) META.variant = bl.variant;
      });
    });
    LC.skin = META.template;
    LC.color = META.theme_color;
    LC.variant = META.variant;
    LC.layout = META.layout;
    PGS.loaded = true;
    let want = wsPendingPage() || (page || "home");
    if (!PAGES.filter((p) => p.slug === want).length) want = "home";
    sel.value = want;
    renderPageList();
    renderGallery();
    return want;
  }
  function loadDraft(first, goSlug) {
    return ctx.api("/api/site/draft?brand_id=" + encodeURIComponent(brandId)).then((d) => {
      if (!alive) return;
      const doc = d && d.draft ? d.draft : null;
      if (!doc) { say(t("ws_no_site")); return; }
      if (first) { famOpen = ""; page = "home"; }
      const want = applyDraft(doc);
      const go = goSlug || want;
      if (go !== want && sel && PAGES.filter((p) => p.slug === go).length) { sel.value = go; page = go; }
      loadCanvas(go);
      loadStatus();
      if (first) {
        ctx.api("/api/site/themes").then((tj) => {
          if (!alive) return;
          if (tj && tj.ids) { THEMES.ids = tj.ids; THEMES.templates = tj.templates || {}; THEMES.families = tj.families || []; THEMES["default"] = tj["default"] || ""; renderGallery(); }
        }).catch(() => { /* gallery falls back to flat list */ });
      }
    }).catch(() => { /* draft fetch failure — keep current view */ });
  }
  function syncDraftMeta() {
    return ctx.api("/api/site/draft?brand_id=" + encodeURIComponent(brandId)).then((d) => {
      if (!alive || !(d && d.draft)) return;
      applyDraft(d.draft);
    }).catch(() => { /* best-effort */ });
  }
  loadDraft(true, null);

  root.querySelectorAll(".ws-tab").forEach((tb) => {
    tb.addEventListener("click", () => {
      const nm = tb.getAttribute("data-tab");
      tab = nm;
      root.querySelectorAll(".ws-tab").forEach((x) => { x.classList.toggle("on", x === tb); });
      const pp = root.querySelector(".ws-ppane"), dp = root.querySelector(".ws-dpane");
      if (pp) pp.style.display = nm === "pages" ? "" : "none";
      if (dp) dp.style.display = nm === "design" ? "" : "none";
    });
  });
  // DESIGN is the default tab (PT: 设计用得最多); an explicit Pages choice sticks.
  if ((tab || "design") === "design") {
    const dt = root.querySelector('.ws-tab[data-tab="design"]');
    if (dt) dt.click();
  }
  ctx.api("/api/sites").then((d) => {
    if (!alive) return;
    const s = ((d && d.sites) || []).filter((x) => String(x.brand_id) === String(brandId))[0];
    if (s && s.status === "published" && s.live_url) {
      const lv = root.querySelector(".ws-live");
      if (lv) { lv.href = s.live_url; lv.style.display = ""; }
    }
  }).catch(() => { /* live link is optional */ });
  sel.addEventListener("change", () => { loadCanvas(sel.value); });

  function setWsMode(m) {
    mode = m;
    root.querySelectorAll(".ws-m[data-m]").forEach((x) => { x.classList.toggle("on", x.getAttribute("data-m") === m); });
    say(m === "text" ? t("ws_text_hint") : t("ws_view_hint"));
    loadCanvas(page);
    if (m === "text") setTimeout(() => {
      const fd = frame.contentDocument;
      if (!fd || !fd.body) return;
      wsAnnotateCanvas(fd);
      wsLockCanvas(fd);
    }, 0);
  }
  root.querySelectorAll(".ws-m[data-m]").forEach((mb) => { mb.addEventListener("click", () => { setWsMode(mb.getAttribute("data-m")); }); });
  const am = root.querySelector(".ws-askmaya");
  if (am) {
    am.addEventListener("click", () => {
      if (opts.focusChat) opts.focusChat();
    });
  }
  const _ap = root.querySelector(".ws-addpage");
  if (_ap) _ap.addEventListener("click", () => { wsSay(t("ws_say_addpage")); });
  const _ab = root.querySelector(".ws-addblog");
  if (_ab) _ab.addEventListener("click", () => { wsSay(t("ws_say_addblog")); });
  const _ak = root.querySelector(".ws-addblock");
  if (_ak) _ak.addEventListener("click", () => {
    const dt = root.querySelector('.ws-tab[data-tab="pages"]');
    if (dt) dt.click();
    PGS.badd = true;
    renderBlockList();
    const bp = root.querySelector(".ws-bpick");
    if (bp && bp.scrollIntoView) { try { bp.scrollIntoView({ block: "nearest" }); } catch (_) { /* detached */ } }
  });

  function loadCanvas(pg) {
    page = pg || "home";
    let src = ctx.prefix() + "/api/site/preview?brand_id=" + encodeURIComponent(brandId) + "&page=" + encodeURIComponent(page);
    if (wsPendingEditLive() && pendingEdit.token) src += "&edit_token=" + encodeURIComponent(pendingEdit.token);
    frame.src = src;
    if (_heroAuto) _heroAuto = false;
    else { _heroN = 0; if (_heroT) { clearTimeout(_heroT); _heroT = null; } }
    try { renderPageList(); } catch (_) { /* list refresh is cosmetic */ }
  }

  async function recoverUnavailablePreview(token) {
    if (!alive || previewRecoveryInFlight || previewRecoveryToken === String(token || "")) return;
    previewRecoveryToken = String(token || "");
    previewRecoveryInFlight = true;
    say(t("ws_preview_refresh_sub"));
    ctx.toast(t("ws_preview_refresh_title"), t("ws_preview_refresh_sub"), "info");
    const old = pendingEdit;
    try {
      const latest = await ctx.api("/api/site/edit/pending?brand_id=" + encodeURIComponent(brandId));
      if (!alive) return;
      if (old && opts.onPendingResolved) opts.onPendingResolved(old.token, "gone");
      if (latest && latest.pending && latest.token && String(latest.token) !== String(token)) {
        pendingEdit = { brand_id: String(brandId), token: String(latest.token), blocks: Array.isArray(latest.preview) ? latest.preview : [] };
        const nextPage = wsPendingPage();
        if (nextPage) {
          page = nextPage;
          if (sel) sel.value = nextPage;
        }
      } else {
        pendingEdit = null;
        pendingInline = false;
        pendingShown = null;
      }
    } catch (_) {
      if (!alive) return;
      if (old && opts.onPendingResolved) opts.onPendingResolved(old.token, "gone");
      pendingEdit = null;
      pendingInline = false;
      pendingShown = null;
    } finally {
      previewRecoveryInFlight = false;
      if (alive) loadCanvas(page || "home");
    }
  }

  frame.addEventListener("load", () => {
    const fd = frame.contentDocument;
    if (!fd || !fd.body) return;
    if (isPendingPreviewUnavailableDocument(fd) && wsPendingEditLive() && pendingEdit.token) {
      frame.style.visibility = "hidden";
      recoverUnavailablePreview(pendingEdit.token);
      return;
    }
    frame.style.visibility = "";
    applySkinLive();
    applyColorLive();
    wsHeroWatch(fd);
    try {
      const slugs = {};
      for (let i = 0; i < sel.options.length; i++) slugs[sel.options[i].value] = 1;
      fd.querySelectorAll("a[href]").forEach((a) => {
        a.addEventListener("click", (ev) => {
          let u;
          try { u = new URL(a.getAttribute("href") || "", frame.contentWindow.location.href); } catch (_) { return; }
          if (u.origin !== frame.contentWindow.location.origin) return;
          ev.preventDefault();
          const seg = u.pathname.split("/").filter(Boolean);
          const bi = seg.indexOf("brands");
          let sg = "home";
          if (bi >= 0) { const af = seg.slice(bi + 2); sg = af.length ? af[af.length - 1] : "home"; } else if (seg.length) sg = seg[seg.length - 1];
          if (!slugs[sg]) sg = "home";
          if (sel.value !== sg) sel.value = sg;
          loadCanvas(sg);
        });
      });
    } catch (_) { /* nav interception is best-effort */ }
    wsImageAction(fd);
    if (mode === "text") { wsAnnotateCanvas(fd); wsLockCanvas(fd); }
    if (wsPendingEditLive()) wsPaintPending(fd);
    if (wsPendingEditLive()) wsRevealPending();
    else say(mode === "text" ? t("ws_text_hint") : t("ws_view_hint"));
  });

  // Text mode is Maya-first: targeting affordances over the same clean design shown by View.
  const WS_TEXT_SEL = "[data-soa-node-id],[data-field],h1,h2,h3,h4,p,dt,dd,blockquote,figcaption,th,td";
  function wsAnnotateCanvas(fd) {
    try {
      if (!fd || !fd.body || fd.body.getAttribute("data-soa-wb-annotated") === "1") return;
      const st = fd.createElement("style");
      st.textContent = "[data-soa-wb-block]{position:relative;outline:2px solid transparent;outline-offset:3px}"
        + "[data-soa-wb-block]:hover,[data-soa-wb-block]:focus-within{outline-color:#2847C4}"
        + "[data-soa-wb-block]::before{content:attr(data-soa-wb-id);position:absolute;z-index:2147482000;right:8px;top:-24px;"
        + "max-width:calc(100% - 16px);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:4px 7px;border-radius:5px;background:#2847C4;color:#fff;"
        + "font:600 11px/1.3 system-ui,-apple-system,sans-serif;opacity:0;pointer-events:none;transform:translateY(-3px);transition:opacity .12s ease,transform .12s ease}"
        + "[data-soa-wb-block]:hover::before,[data-soa-wb-block]:focus-within::before{opacity:1;transform:none}";
      fd.head.appendChild(st);
      fd.querySelectorAll("[data-block-id],[data-soa-block-id]").forEach((rt) => {
        const id = rt.getAttribute("data-block-id") || rt.getAttribute("data-soa-block-id") || "";
        if (!id) return;
        rt.setAttribute("data-soa-wb-block", "1");
        rt.setAttribute("data-soa-wb-id", id);
      });
      fd.body.setAttribute("data-soa-wb-annotated", "1");
    } catch (_) { /* best-effort chrome */ }
  }
  let wsClearDeleteConfirmRef = null;
  function wsLockCanvas(fd) {
    try {
      if (!fd || !fd.body || fd.body.getAttribute("data-soa-locked") === "1") return;
      const st = fd.createElement("style");
      st.textContent = "[data-soa-text-host]{border-radius:4px;transition:opacity .12s ease,outline-color .12s ease}"
        + "[data-soa-text-host]:hover,[data-soa-text-host]:focus{outline:2px solid #2847C4!important;outline-offset:3px!important;opacity:.88!important}"
        + "[data-soa-wb-block]::before{display:none!important}"
        + ".soa-wb-text-actions{all:initial!important;box-sizing:border-box!important;position:fixed!important;z-index:2147483500!important;display:flex!important;align-items:center!important;gap:6px!important}"
        + ".soa-wb-text-actions[hidden]{display:none!important}.soa-wb-text-actions button,.soa-wb-block-actions button,.soa-wb-block-confirm button{all:unset!important;box-sizing:border-box!important;min-height:36px!important;padding:8px 11px!important;border:1px solid rgba(18,24,40,.22)!important;border-radius:6px!important;background:#fff!important;color:#202638!important;box-shadow:0 4px 14px rgba(0,0,0,.18)!important;font:700 12px/1 system-ui,-apple-system,sans-serif!important;letter-spacing:0!important;white-space:nowrap!important;cursor:pointer!important}"
        + ".soa-wb-text-actions button:hover,.soa-wb-block-actions button:hover,.soa-wb-block-confirm button:hover{background:#f8fafc!important}.soa-wb-text-actions button:focus-visible,.soa-wb-block-actions button:focus-visible,.soa-wb-block-confirm button:focus-visible{outline:3px solid #2847C4!important;outline-offset:2px!important}"
        + ".soa-wb-text-actions button:disabled,.soa-wb-block-actions button:disabled,.soa-wb-block-confirm button:disabled{cursor:wait!important;opacity:.62!important}.soa-wb-text-actions button[hidden],.soa-wb-block-actions button[hidden]{display:none!important}"
        + ".soa-wb-text-maya,.soa-wb-block-add{border-color:#2847C4!important;color:#2847C4!important}.soa-wb-text-save{border-color:#1f7a4d!important;background:#166534!important;color:#fff!important}"
        + ".soa-wb-text-delete,.soa-wb-block-del{border-color:#b42318!important;color:#b42318!important}"
        + ".soa-wb-block-actions{all:initial!important;box-sizing:border-box!important;position:absolute!important;z-index:2147483400!important;top:8px!important;right:8px!important;display:none!important;align-items:center!important;gap:6px!important}"
        + "[data-soa-wb-block]:hover>.soa-wb-block-actions,[data-soa-wb-block]:focus-within>.soa-wb-block-actions{display:flex!important}"
        + ".soa-wb-block-confirm{all:initial!important;box-sizing:border-box!important;position:fixed!important;z-index:2147483600!important;display:flex!important;flex-direction:column!important;gap:8px!important;min-width:220px!important;max-width:min(320px,calc(100% - 16px))!important;padding:10px 12px!important;border:1px solid rgba(180,35,24,.28)!important;border-radius:8px!important;background:#fff!important;box-shadow:0 8px 24px rgba(0,0,0,.18)!important;color:#202638!important;font:600 13px/1.4 system-ui,-apple-system,sans-serif!important}"
        + ".soa-wb-block-confirm[hidden]{display:none!important}"
        + ".soa-wb-block-confirm b{all:unset!important;display:block!important;font:700 13px/1.35 system-ui,-apple-system,sans-serif!important}"
        + ".soa-wb-block-confirm span{all:unset!important;display:block!important;color:#5b6475!important;font:500 12px/1.4 system-ui,-apple-system,sans-serif!important}"
        + ".soa-wb-block-confirm .soa-wb-block-confirm-acts{all:unset!important;display:flex!important;gap:6px!important;justify-content:flex-end!important}"
        + "[data-soa-wb-confirming]{outline:2px solid #b42318!important;outline-offset:3px!important;background:rgba(254,226,226,.35)!important}"
        + "[data-soa-wb-focus]{outline:2px solid #2847C4!important;outline-offset:3px!important}"
        + "[data-soa-inline-editing]{outline:2px solid #1f7a4d!important;outline-offset:3px!important;background:rgba(220,252,231,.28)!important;cursor:text!important}";
      st.id = "soa-wb-text-style";
      fd.head.appendChild(st);
      const hits = [];
      let active = null, lastHit = null, activeCancel = null, hideTimer = null, pendingDel = null;
      const ADDABLE = { prose: 1, faq: 1, compare: 1, data_facts: 1, entity: 1, howto: 1, pricing: 1, testimonials: 1 };
      fd.querySelectorAll("[data-block-id],[data-soa-block-id]").forEach((rt) => {
        const ty = rt.getAttribute("data-block-type") || rt.getAttribute("data-soa-block-type") || "";
        const why = ty === "article" || ty === "blog_index" ? "blog" : "copy";
        if (!rt.querySelector(".soa-wb-block-actions")) {
          const bbar = fd.createElement("div");
          bbar.className = "soa-wb-block-actions";
          bbar.setAttribute("contenteditable", "false");
          if (ADDABLE[ty]) {
            const add = fd.createElement("button");
            add.type = "button";
            add.className = "soa-wb-block-add";
            add.textContent = t("ws_block_add_version");
            bbar.appendChild(add);
            add.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); wsClearDeleteConfirm(); wsBlockMutate(rt, "add", ty); });
          }
          if (ty !== "hero" && ty !== "hero_visual") {
            const up = fd.createElement("button");
            up.type = "button";
            up.className = "soa-wb-block-up";
            up.textContent = "↑";
            up.title = t("ws_bl_up");
            up.disabled = !wsCanMoveBlock(rt, "up");
            bbar.appendChild(up);
            const dn = fd.createElement("button");
            dn.type = "button";
            dn.className = "soa-wb-block-down";
            dn.textContent = "↓";
            dn.title = t("ws_bl_dn");
            dn.disabled = !wsCanMoveBlock(rt, "down");
            bbar.appendChild(dn);
            up.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); wsClearDeleteConfirm(); wsBlockMutate(rt, "up"); });
            dn.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); wsClearDeleteConfirm(); wsBlockMutate(rt, "down"); });
          }
          const bdel = fd.createElement("button");
          bdel.type = "button";
          bdel.className = "soa-wb-block-del";
          bdel.textContent = t("ws_block_delete");
          bbar.appendChild(bdel);
          bdel.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); wsAskDelete(fd, { kind: "block", el: rt, root: rt }); });
          rt.appendChild(bbar);
        }
        rt.querySelectorAll(WS_TEXT_SEL).forEach((el) => {
          if (!(el.innerText || "").trim() || el.closest(".soa-wb-reimage,.soa-se-badge,.soa-wb-block-actions,.soa-wb-block-confirm")) return;
          if (el.querySelector && el.querySelector(WS_TEXT_SEL)) return;
          if (hits.some((hit) => hit.el === el)) return;
          hits.push({ el, root: rt, why });
        });
      });
      const pageRoot = fd.querySelector("main[data-soa-page-id]");
      const pageTitle = pageRoot && pageRoot.querySelector('[data-soa-page-field="title"]');
      if (pageRoot && pageTitle && (pageTitle.innerText || "").trim()) hits.push({ el: pageTitle, root: pageRoot, why: "page" });
      const bar = fd.createElement("div");
      bar.className = "soa-wb-text-actions";
      bar.setAttribute("contenteditable", "false");
      bar.hidden = true;
      const mayaBtn = fd.createElement("button");
      mayaBtn.type = "button";
      mayaBtn.className = "soa-wb-text-maya";
      mayaBtn.textContent = t("ws_paragraph_maya");
      const edit = fd.createElement("button");
      edit.type = "button";
      edit.className = "soa-wb-text-edit";
      edit.textContent = t("ws_paragraph_edit");
      const del = fd.createElement("button");
      del.type = "button";
      del.className = "soa-wb-text-delete";
      del.textContent = t("ws_paragraph_delete");
      const save = fd.createElement("button");
      save.type = "button";
      save.className = "soa-wb-text-save";
      save.textContent = t("ws_edit_save");
      save.hidden = true;
      const cancel = fd.createElement("button");
      cancel.type = "button";
      cancel.className = "soa-wb-text-cancel";
      cancel.textContent = t("ws_edit_cancel");
      cancel.hidden = true;
      bar.appendChild(mayaBtn);
      bar.appendChild(edit);
      bar.appendChild(del);
      bar.appendChild(save);
      bar.appendChild(cancel);
      fd.body.appendChild(bar);
      const confirmBox = fd.createElement("div");
      confirmBox.className = "soa-wb-block-confirm";
      confirmBox.setAttribute("contenteditable", "false");
      confirmBox.hidden = true;
      const confirmTitle = fd.createElement("b");
      const confirmNote = fd.createElement("span");
      const confirmActs = fd.createElement("div");
      confirmActs.className = "soa-wb-block-confirm-acts";
      const confirmNo = fd.createElement("button");
      confirmNo.type = "button";
      confirmNo.textContent = t("ws_pg_cancel");
      const confirmOk = fd.createElement("button");
      confirmOk.type = "button";
      confirmOk.className = "soa-wb-block-del";
      confirmOk.textContent = t("ws_block_delete_confirm");
      confirmActs.appendChild(confirmNo);
      confirmActs.appendChild(confirmOk);
      confirmBox.appendChild(confirmTitle);
      confirmBox.appendChild(confirmNote);
      confirmBox.appendChild(confirmActs);
      fd.body.appendChild(confirmBox);
      function placeBar() {
        if (!active || bar.hidden) return;
        const r = active.el.getBoundingClientRect();
        const w = bar.offsetWidth || 190, h = bar.offsetHeight || 36, vw = fd.documentElement.clientWidth || 360, vh = fd.documentElement.clientHeight || 640;
        bar.style.setProperty("left", Math.max(8, Math.min(r.left, vw - w - 8)) + "px", "important");
        bar.style.setProperty("top", Math.max(8, r.bottom + h + 8 <= vh ? r.bottom + 6 : r.top - h - 6) + "px", "important");
      }
      function placeConfirm() {
        if (!pendingDel || confirmBox.hidden) return;
        const r = pendingDel.el.getBoundingClientRect();
        const w = confirmBox.offsetWidth || 240, h = confirmBox.offsetHeight || 96, vw = fd.documentElement.clientWidth || 360, vh = fd.documentElement.clientHeight || 640;
        confirmBox.style.setProperty("left", Math.max(8, Math.min(r.left, vw - w - 8)) + "px", "important");
        confirmBox.style.setProperty("top", Math.max(8, r.bottom + h + 8 <= vh ? r.bottom + 6 : r.top - h - 6) + "px", "important");
      }
      function show(hit) {
        if (activeCancel || pendingDel) return;
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        active = hit;
        lastHit = hit;
        del.hidden = hit.why === "page";
        bar.hidden = false;
        placeBar();
      }
      function hideSoon(ev) {
        if (activeCancel || pendingDel) return;
        if (ev && ev.relatedTarget && (ev.relatedTarget === bar || (bar.contains && bar.contains(ev.relatedTarget)))) return;
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => { bar.hidden = true; active = null; lastHit = null; hideTimer = null; }, 3000);
      }
      hits.forEach((hit) => {
        hit.el.setAttribute("data-soa-text-host", "1");
        hit.el.setAttribute("contenteditable", "false");
        hit.el.setAttribute("tabindex", "0");
        hit.el.addEventListener("mouseenter", () => { show(hit); });
        hit.el.addEventListener("mouseleave", hideSoon);
        hit.el.addEventListener("focus", () => { show(hit); });
        hit.el.addEventListener("pointerdown", () => { show(hit); });
        hit.el.addEventListener("click", () => { if (!activeCancel && !pendingDel) show(hit); });
      });
      bar.addEventListener("mouseenter", () => { if (!active && lastHit) active = lastHit; if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; } });
      bar.addEventListener("mouseleave", hideSoon);
      bar.addEventListener("click", (ev) => { ev.stopPropagation(); });
      confirmBox.addEventListener("click", (ev) => { ev.stopPropagation(); });
      mayaBtn.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = active || lastHit;
        if (target) wsAskMayaText(target.el, target.root, target.why);
        bar.hidden = true;
        active = null;
        lastHit = null;
      });
      edit.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = active || lastHit;
        if (!target) return;
        const editing = target;
        if (activeCancel) activeCancel();
        active = editing;
        activeCancel = wsTextEdit(fd, editing.el, editing.root, bar, () => { activeCancel = null; active = null; lastHit = null; bar.hidden = true; });
      });
      del.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = active || lastHit;
        if (!target || target.why === "page") return;
        wsAskDelete(fd, { kind: "text", el: target.el, root: target.root });
      });
      confirmNo.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); wsClearDeleteConfirm(); });
      confirmOk.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (!pendingDel) return;
        confirmOk.disabled = true;
        confirmNo.disabled = true;
        if (pendingDel.kind === "block") wsBlockMutate(pendingDel.root, "delete");
        else wsTextDelete(pendingDel.el, pendingDel.root, confirmOk);
      });
      if (fd.defaultView) {
        fd.defaultView.addEventListener("scroll", () => { placeBar(); placeConfirm(); });
        fd.defaultView.addEventListener("resize", () => { placeBar(); placeConfirm(); });
      }
      function wsClearDeleteConfirm() {
        if (pendingDel && pendingDel.el) pendingDel.el.removeAttribute("data-soa-wb-confirming");
        pendingDel = null;
        confirmBox.hidden = true;
        confirmOk.disabled = false;
        confirmNo.disabled = false;
      }
      function wsAskDelete(fdoc, target) {
        if (!fdoc || !target || !target.el) return;
        if (target.el.closest && target.el.closest("[data-soa-siteedit-pending]")) { ctx.toast(t("ws_edit_pending_target"), "", "warn"); return; }
        wsClearDeleteConfirm();
        pendingDel = target;
        target.el.setAttribute("data-soa-wb-confirming", "1");
        bar.hidden = true;
        active = null;
        confirmTitle.textContent = t(target.kind === "block" ? "ws_block_delq" : "ws_paragraph_delq");
        confirmNote.textContent = t(target.kind === "block" ? "ws_block_delnote" : "ws_paragraph_delnote");
        confirmBox.hidden = false;
        placeConfirm();
      }
      wsClearDeleteConfirmRef = wsClearDeleteConfirm;
      fd.body.setAttribute("data-soa-locked", "1");
    } catch (_) { /* best-effort chrome */ }
  }
  function wsAskMayaText(el, rt, why) {
    if (!el) return;
    if (el.closest && el.closest("[data-soa-siteedit-pending]")) { ctx.toast(t("ws_edit_pending_target"), "", "warn"); return; }
    const snip = (el.innerText || "").trim().slice(0, 90);
    if (why === "blog") {
      const ti = rt ? rt.querySelector('[data-field="title"]') : null;
      wsSay(wsFill("ws_say_lockart", { s: ((ti && ti.innerText) || snip).trim().slice(0, 90) }), wsBlockContext(el, rt, snip));
    } else {
      wsSay(wsFill("ws_say_lockcopy", { p: page || "home", s: snip }), wsBlockContext(el, rt, snip));
    }
  }
  function wsBumpDraft(j) {
    if (j && j.page_id != null && j.page_version != null) PAGES.forEach((p) => { if (String(p.page_id) === String(j.page_id)) p.draft_version = j.page_version; });
    if (j && j.superseded_pending && pendingEdit) {
      const oldp = pendingEdit;
      if (opts.onPendingResolved) opts.onPendingResolved(oldp.token, "gone");
      clearPending(brandId, oldp.token);
    }
  }
  function wsAfterDraftMutation(j, pageSlug) {
    wsBumpDraft(j);
    return loadDraft(false, pageSlug || page);
  }
  function wsRemoveLocal(el) {
    if (!el) return;
    const node = el.closest ? el.closest("[data-soa-node-id]") : null;
    const victim = node || el;
    if (victim && victim.parentNode) victim.parentNode.removeChild(victim);
  }
  function wsDropLocalBlock(rt) {
    const id = rt && (rt.getAttribute("data-block-id") || rt.getAttribute("data-soa-block-id"));
    if (rt && rt.parentNode) rt.parentNode.removeChild(rt);
    const pg = PAGES.filter((p) => p.slug === (page || "home"))[0];
    if (pg && id) {
      pg.bl = (pg.bl || []).filter((b) => String(b.id) !== String(id));
      pg.blocks = pg.bl.length;
      delete BLOCKMETA[id];
      try { renderPageList(); } catch (_) { /* detached */ }
    }
  }
  function wsPageBlocks(doc) {
    if (!doc) return [];
    return Array.prototype.filter.call(doc.querySelectorAll("[data-block-id],[data-soa-block-id]"), (el) => !el.closest || !el.parentElement || !el.parentElement.closest || !el.parentElement.closest("[data-block-id],[data-soa-block-id]"));
  }
  function wsCanMoveBlock(rt, dir) {
    if (!rt) return false;
    const ty = rt.getAttribute("data-block-type") || rt.getAttribute("data-soa-block-type") || "";
    if (ty === "hero" || ty === "hero_visual") return false;
    const all = wsPageBlocks(rt.ownerDocument);
    const i = all.indexOf(rt);
    const dest = i + (dir === "up" ? -1 : 1);
    if (i < 0 || dest < 0 || dest >= all.length) return false;
    const oty = all[dest].getAttribute("data-block-type") || all[dest].getAttribute("data-soa-block-type") || "";
    return oty !== "hero" && oty !== "hero_visual";
  }
  function wsSyncMoveButtons(fd) {
    if (!fd) return;
    fd.querySelectorAll("[data-soa-wb-block]").forEach((rt) => {
      const up = rt.querySelector(".soa-wb-block-up"), dn = rt.querySelector(".soa-wb-block-down");
      if (up) up.disabled = !wsCanMoveBlock(rt, "up");
      if (dn) dn.disabled = !wsCanMoveBlock(rt, "down");
    });
  }
  function wsRememberBlock(id, type, afterId) {
    const pg = PAGES.filter((p) => p.slug === (page || "home"))[0];
    if (!pg) return;
    pg.bl = pg.bl || [];
    const item = { id, type: type || "", label: type || "" };
    let idx = -1;
    if (afterId) pg.bl.forEach((b, i) => { if (String(b.id) === String(afterId)) idx = i; });
    if (idx >= 0) pg.bl.splice(idx + 1, 0, item);
    else pg.bl.push(item);
    pg.blocks = pg.bl.length;
    if (id) BLOCKMETA[id] = { page: pg.slug, type: type || "", label: type || "" };
    try { renderPageList(); } catch (_) { /* detached */ }
  }
  function wsReorderLocalBlocks(blockId, dir) {
    const pg = PAGES.filter((p) => p.slug === (page || "home"))[0];
    if (!pg || !pg.bl) return;
    let idx = -1;
    pg.bl.forEach((b, i) => { if (String(b.id) === String(blockId)) idx = i; });
    const dest = idx + (dir === "up" ? -1 : 1);
    if (idx < 0 || dest < 0 || dest >= pg.bl.length) return;
    const tmp = pg.bl[idx];
    pg.bl[idx] = pg.bl[dest];
    pg.bl[dest] = tmp;
    try { renderPageList(); } catch (_) { /* detached */ }
  }
  function wsRefreshTextChrome(fd) {
    if (!fd || !fd.body) return;
    fd.body.removeAttribute("data-soa-locked");
    fd.body.removeAttribute("data-soa-wb-annotated");
    const st = fd.getElementById("soa-wb-text-style");
    if (st) st.remove();
    fd.querySelectorAll(".soa-wb-text-actions,.soa-wb-block-actions,.soa-wb-block-confirm").forEach((el) => el.remove());
    fd.querySelectorAll("[data-soa-wb-block]").forEach((el) => { el.removeAttribute("data-soa-wb-block"); el.removeAttribute("data-soa-wb-id"); });
    wsAnnotateCanvas(fd);
    wsLockCanvas(fd);
  }
  function wsFocusBlock(el) {
    if (!el) return;
    try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) { try { el.scrollIntoView(true); } catch { /* detached */ } }
    el.setAttribute("data-soa-wb-focus", "1");
    setTimeout(() => { if (el && el.removeAttribute) el.removeAttribute("data-soa-wb-focus"); }, 1800);
  }
  function wsInsertAddedBlock(rt, addedId, blockType) {
    if (!rt || !addedId) return Promise.resolve();
    const url = ctx.prefix() + "/api/site/preview?brand_id=" + encodeURIComponent(brandId) + "&page=" + encodeURIComponent(page || "home");
    return fetch(url, { credentials: "same-origin" }).then((r) => { if (!r || !r.ok) throw new Error("preview"); return r.text(); }).then((htmlText) => {
      const doc = new DOMParser().parseFromString(htmlText, "text/html");
      const q = '[data-block-id="' + String(addedId).replace(/"/g, "") + '"],[data-soa-block-id="' + String(addedId).replace(/"/g, "") + '"]';
      const fresh = doc.querySelector(q);
      if (!fresh || !rt.parentNode) throw new Error("missing-block");
      const node = rt.ownerDocument.importNode(fresh, true);
      if (rt.nextSibling) rt.parentNode.insertBefore(node, rt.nextSibling);
      else rt.parentNode.appendChild(node);
      wsRememberBlock(addedId, blockType, rt.getAttribute("data-block-id") || rt.getAttribute("data-soa-block-id"));
      wsRefreshTextChrome(rt.ownerDocument);
      const liveEl = rt.ownerDocument.querySelector(q);
      wsFocusBlock(liveEl || node);
    });
  }
  function wsMoveLocalBlock(rt, dir) {
    if (!rt || !rt.parentNode) return;
    const all = wsPageBlocks(rt.ownerDocument);
    const i = all.indexOf(rt);
    const dest = i + (dir === "up" ? -1 : 1);
    if (i < 0 || dest < 0 || dest >= all.length) return;
    if (dir === "up") rt.parentNode.insertBefore(rt, all[dest]);
    else if (all[dest].nextSibling) rt.parentNode.insertBefore(rt, all[dest].nextSibling);
    else rt.parentNode.appendChild(rt);
    wsReorderLocalBlocks(rt.getAttribute("data-block-id") || rt.getAttribute("data-soa-block-id"), dir);
    wsSyncMoveButtons(rt.ownerDocument);
    wsFocusBlock(rt);
  }
  function wsTextDelete(el, rt, btn) {
    if (!el) return;
    if (el.closest && el.closest("[data-soa-siteedit-pending]")) { ctx.toast(t("ws_edit_pending_target"), "", "warn"); return; }
    const context = wsBlockContext(el, rt, (el.innerText || "").trim());
    if (!context || context.page_id == null || context.page_version == null || !context.block_id || !context.node_id) {
      ctx.toast(t("ws_edit_fail"), "", "warn");
      if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef();
      return;
    }
    if (btn) btn.disabled = true;
    wsEnqueue("text:del:" + context.node_id, () => ctx.api("/api/site/text/delete", { method: "POST", json: {
      brand_id: brandId, page_id: context.page_id, page_version: context.page_version, block_id: context.block_id, node_id: context.node_id, field: context.field, expected_text: context.expected_text,
    } }).then((j) => {
      if (btn) btn.disabled = false;
      if (j && (j.upgrade || j.error === "upgrade")) { if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (j && j.ok) { wsBumpDraft(j); wsRemoveLocal(el); if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.toast(t("ws_paragraph_deleted"), "", "ok"); return; }
      if (j && (j.stale || j.error === "stale")) { if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.toast(t("ws_edit_stale"), "", "warn"); loadCanvas(context.page); return; }
      if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef();
      ctx.toast(t(j && j.error === "not_deletable" ? "ws_paragraph_not_deletable" : "ws_edit_fail"), "", "warn");
    }).catch(() => { if (btn) btn.disabled = false; if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.toast(t("ws_edit_fail"), "", "warn"); }));
  }
  function wsBlockMutate(rt, act, blockType) {
    if (!rt) return;
    if (rt.closest && rt.closest("[data-soa-siteedit-pending]")) { ctx.toast(t("ws_edit_pending_target"), "", "warn"); return; }
    const context = wsBlockContext(rt, rt, "");
    if (!context || context.page_id == null || context.page_version == null || !context.block_id) { ctx.toast(t("ws_block_fail"), "", "warn"); if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); return; }
    const moving = act === "up" || act === "down";
    const path = act === "add" ? "/api/site/block/add" : (act === "delete" ? "/api/site/block/delete" : "/api/site/block/move");
    const payload = { brand_id: brandId, page_id: context.page_id, page_version: context.page_version, block_id: context.block_id };
    if (act === "add") { payload.after_block_id = context.block_id; payload.block_type = blockType || context.block_type; }
    if (moving) payload.direction = act;
    wsEnqueue("block:" + act + ":" + context.block_id, () => ctx.api(path, { method: "POST", json: payload }).then((j) => {
      if (j && (j.upgrade || j.error === "upgrade")) { if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
      if (j && j.ok) {
        wsBumpDraft(j);
        if (act === "delete") { wsDropLocalBlock(rt); if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.toast(t("ws_block_deleted"), "", "ok"); return; }
        if (moving) { wsMoveLocalBlock(rt, act); ctx.toast(t(act === "up" ? "ws_block_moved_up" : "ws_block_moved_down"), "", "ok"); return; }
        ctx.toast(t("ws_block_added"), "", "ok");
        return wsInsertAddedBlock(rt, j.added_block_id, payload.block_type).catch(() => loadCanvas(context.page));
      }
      if (j && (j.stale || j.error === "stale")) { if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.toast(t("ws_edit_stale"), "", "warn"); loadCanvas(context.page); return; }
      if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef();
      ctx.toast(t(j && j.error === "cannot_move" ? "ws_block_cannot_move" : "ws_block_fail"), "", "warn");
    }).catch(() => { if (wsClearDeleteConfirmRef) wsClearDeleteConfirmRef(); ctx.toast(t("ws_block_fail"), "", "warn"); }));
  }
  function wsTextEdit(fd, el, rt, bar, onDone) {
    if (!fd || !el || !bar) return null;
    if (el.closest && el.closest("[data-soa-siteedit-pending]")) { ctx.toast(t("ws_edit_pending_target"), "", "warn"); return null; }
    const context = wsBlockContext(el, rt, (el.innerText || "").trim());
    if (!context || context.page_id == null || context.page_version == null || !context.block_id || !context.node_id) { ctx.toast(t("ws_edit_fail"), "", "warn"); return null; }
    const mayaB = bar.querySelector(".soa-wb-text-maya"), edit = bar.querySelector(".soa-wb-text-edit"), del = bar.querySelector(".soa-wb-text-delete"), save = bar.querySelector(".soa-wb-text-save"), cancel = bar.querySelector(".soa-wb-text-cancel");
    let closed = false;
    const original = (el.innerText || "").trim();
    const originalHtml = el.innerHTML;
    context.expected_text = original;
    mayaB.hidden = true;
    edit.hidden = true;
    if (del) del.hidden = true;
    save.hidden = false;
    save.disabled = true;
    cancel.hidden = false;
    el.setAttribute("contenteditable", "true");
    el.setAttribute("data-soa-inline-editing", "1");
    el.spellcheck = true;
    function value() { return (el.innerText || "").trim(); }
    function changed() { save.disabled = value() === original; }
    function blockClick(ev) { if (ev.target.closest && ev.target.closest("a")) ev.preventDefault(); }
    function cleanup(restore) {
      if (closed) return;
      closed = true;
      if (restore) el.innerHTML = originalHtml;
      el.setAttribute("contenteditable", "false");
      el.removeAttribute("data-soa-inline-editing");
      el.removeEventListener("input", changed);
      el.removeEventListener("keydown", keys);
      el.removeEventListener("click", blockClick, true);
      save.onclick = null;
      cancel.onclick = null;
      mayaB.hidden = false;
      edit.hidden = false;
      if (del) del.hidden = false;
      save.hidden = true;
      save.disabled = false;
      save.textContent = t("ws_edit_save");
      cancel.hidden = true;
      cancel.disabled = false;
      if (onDone) onDone();
    }
    function keys(ev) {
      if (ev.key === "Escape") { ev.preventDefault(); cleanup(true); } else if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); persist(); }
    }
    function persist() {
      const next = value();
      if (next === original) { cleanup(false); return; }
      save.disabled = true;
      cancel.disabled = true;
      save.textContent = t("ws_edit_saving");
      ctx.api("/api/site/text", { method: "POST", json: {
        brand_id: brandId, page_id: context.page_id, page_version: context.page_version, block_id: context.block_id, node_id: context.node_id, field: context.field, expected_text: original, value: next,
      } }).then((j) => {
        cancel.disabled = false;
        if (j && (j.upgrade || j.error === "upgrade")) { save.disabled = false; save.textContent = t("ws_edit_save"); ctx.upgrade(j.msg, j.url || ctx.prefix() + "/pricing.html"); return; }
        if (j && j.ok) {
          PAGES.forEach((p) => { if (String(p.page_id) === String(j.page_id)) p.draft_version = j.page_version; });
          if (typeof j.value === "string" && value() !== j.value) el.textContent = j.value;
          context.page_version = j.page_version;
          context.expected_text = j.value;
          cleanup(false);
          if (j.superseded_pending && pendingEdit) {
            const oldp = pendingEdit;
            if (opts.onPendingResolved) opts.onPendingResolved(oldp.token, "gone");
            clearPending(brandId, oldp.token);
          }
          ctx.toast(t("ws_edit_saved"), "", "ok");
          return;
        }
        if (j && (j.stale || j.error === "stale")) { cleanup(true); ctx.toast(t("ws_edit_stale"), "", "warn"); loadCanvas(context.page); return; }
        save.disabled = false;
        save.textContent = t("ws_edit_save");
        ctx.toast(t("ws_edit_fail"), "", "warn");
      }).catch(() => { cancel.disabled = false; save.disabled = false; save.textContent = t("ws_edit_save"); ctx.toast(t("ws_edit_fail"), "", "warn"); });
    }
    save.onclick = (ev) => { ev.preventDefault(); ev.stopPropagation(); persist(); };
    cancel.onclick = (ev) => { ev.preventDefault(); ev.stopPropagation(); cleanup(true); };
    el.addEventListener("input", changed);
    el.addEventListener("keydown", keys);
    el.addEventListener("click", blockClick, true);
    try { el.focus(); } catch (_) { /* detached */ }
    return function () { cleanup(true); };
  }

  // Redesign → colour pick → regenerate (same overlay as before, scoped to the drawer)
  root.querySelector(".ws-redesign").addEventListener("click", () => { wsRedesign(); });
  function wsRedesign() {
    if (root.querySelector(".ws-pick")) return;
    const COLORS = [["#2847C4", t("studio_color_indigo")], ["#0E8A6D", t("studio_color_emerald")], ["#BA3B1D", t("studio_color_vermilion")], ["#7A5FB0", t("studio_color_violet")], ["#0F766E", t("studio_color_teal")], ["#B7791F", t("studio_color_amber")], ["#1F2937", t("studio_color_charcoal")]];
    const pick = document.createElement("div");
    pick.className = "ws-pick";
    let inner = '<div class="ws-pkbox"><div class="ws-pkt">' + esc(t("studio_redesign_pick")) + '</div><div class="ws-sw">';
    COLORS.forEach((c) => { inner += '<button class="ws-c" title="' + esc(c[1]) + '" data-c="' + c[0] + '" style="background:' + c[0] + '"></button>'; });
    inner += '</div><button class="ws-keep">' + esc(t("studio_redesign_keep")) + '</button><button class="ws-cancel">' + esc(t("studio_pub_cancel")) + "</button></div>";
    pick.innerHTML = inner;
    pick.addEventListener("click", (ev) => { if (ev.target === pick) pick.remove(); });
    root.appendChild(pick);
    function go(accent) {
      pick.remove();
      const rb = root.querySelector(".ws-redesign");
      if (rb) rb.disabled = true;
      say(t("studio_redesign_working"));
      ctx.api("/api/site/design", { method: "POST", json: { brand_id: brandId, accent: accent || undefined } }).then((j) => wsDesignWait(j)).then((j) => {
        if (rb) rb.disabled = false;
        if (j && j.ok) {
          say(t("studio_redesign_done"));
          try { frame.contentWindow.location.reload(); } catch (_) { frame.src = frame.src; }
        } else if (j && j.upgrade) ctx.upgrade(j.msg, j.url);
        else say(t("studio_redesign_failed").replace("%s", (j && j.error) || ""));
      }).catch(() => { if (rb) rb.disabled = false; say(t("studio_redesign_error").replace("%s", "")); });
    }
    pick.querySelectorAll(".ws-c").forEach((c) => { c.addEventListener("click", () => { go(c.getAttribute("data-c")); }); });
    pick.querySelector(".ws-keep").addEventListener("click", () => { go(""); });
    pick.querySelector(".ws-cancel").addEventListener("click", () => { pick.remove(); });
  }
  function wsDesignPoll(job) {
    return new Promise((resolve, reject) => {
      let tries = 0;
      function tick() {
        tries++;
        ctx.api("/api/site/design/status?job=" + encodeURIComponent(job)).then((d) => {
          if (d && d.state === "done") { resolve(d); return; }
          if (!d || d.error || d.state === "failed" || tries >= SCAN_POLL_MAX) { reject(d || {}); return; }
          setTimeout(tick, SCAN_POLL_MS);
        }).catch((err) => { if (tries >= SCAN_POLL_MAX) { reject(err); return; } setTimeout(tick, SCAN_POLL_MS); });
      }
      setTimeout(tick, SCAN_POLL_MS);
    });
  }
  function wsDesignWait(started) { return started && started.job && started.state === "running" ? wsDesignPoll(started.job) : Promise.resolve(started); }

  // Publish → IMP-G 3-step wizard. The workbench's live link updates on success.
  root.querySelector(".ws-pub").addEventListener("click", () => {
    pwOpen(brandId, (url) => {
      const lv = root.querySelector(".ws-live");
      if (lv && url) { lv.href = url; lv.style.display = ""; }
      say(t("studio_published_url").replace("%s", url));
    }, ctx);
  });

  // ── host API ──
  function setPending(preview) {
    // preview: {brand_id, token, blocks}
    pendingEdit = preview && preview.token ? { brand_id: String(preview.brand_id), token: String(preview.token), blocks: Array.isArray(preview.blocks) ? preview.blocks : [] } : null;
    pendingInline = false;
    pendingShown = null;
    previewRecoveryToken = null;
    if (!alive) return;
    const pg = wsRevealPending();
    page = pg || page;
    loadCanvas(page);
  }
  function clearPending(bid, token, keepCanvas) {
    const p = pendingEdit;
    if (!p) return;
    if (bid != null && String(p.brand_id) !== String(bid)) return;
    if (token && String(p.token) !== String(token)) return;
    const keep = !!(keepCanvas && pendingInline);
    pendingEdit = null;
    pendingInline = false;
    pendingShown = null;
    if (!alive) return;
    if (keep) wsClearPendingPaint();
    else loadCanvas(page || "home");
  }
  function destroy() {
    alive = false;
    if (_heroT) { clearTimeout(_heroT); _heroT = null; }
    const pick = root.querySelector(".ws-pick");
    if (pick) pick.remove();
    root.innerHTML = "";
  }

  return {
    destroy,
    reload: loadCanvas,
    reloadDraft: (first, goSlug) => loadDraft(first, goSlug),
    syncDraftMeta,
    setPending,
    clearPending,
    reimageSoon() {
      // the hero image skill landed → one delayed canvas refresh (the generated image appears on
      // the next render); 75s covers the forced-regenerate case.
      setTimeout(() => { if (alive) loadCanvas(page); }, 75000);
    },
    page: () => page,
    brandName: () => META.brand,
  };
}
